import { binencode } from "@/lib/binary";
import { Address } from "@/protocols/protocol.signal";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { Dexie, Entity, type DexieOptions, type EntityTable } from "dexie";
import bin from "typed-binary";

export enum MessageBodyType {
  MESSAGE = 0,
  GROUP_JOIN = 1,
  GROUP_LEAVE = 2,
  CONTACT_ADD_NOTIFICATION = 3,  // NEW: Bidirectional contact notification
}

export const WiredGroupInviteBody = bin.object({
  groupAddress: Address,
  inviterAddress: Address,
  inviteeAddress: bin.optional(Address),
});

export const WiredGroupInvite = bin.object({
  inviterSignature: bin.u8Array(64),
  body: WiredGroupInviteBody,
});

export const WiredMessageAttachmentEntry = bin.object({
  root: bin.u8Array(32),
  path: bin.string,
  size: bin.u64,
  chunkSize: bin.u32,
});

export const WiredMessageAttachment = bin.object({
  hash: bin.u8Array(32),
  entries: bin.dynamicArrayOf(WiredMessageAttachmentEntry),
});

export async function hashWiredMessageAttachmentEntries(
  entries: bin.Parsed<typeof WiredMessageAttachmentEntry>[],
) {
  const bytes = binencode(
    bin.dynamicArrayOf(WiredMessageAttachmentEntry),
    entries,
  );
  return new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
}
export enum MessageDestinationType {
  DM = 0,
  GROUP = 1,
}

export const WiredMessageBody = bin.genericEnum(
  {
    from: bin.object({
      address: Address,
      nonce: bin.u32,
    }),
    version: bin.u32,
    timestamp: bin.u64,
  },
  {
    [MessageBodyType.MESSAGE]: bin.object({
      to: bin.genericEnum(
        {},
        {
          [MessageDestinationType.DM]: bin.object({
            address: Address,
          }),
          [MessageDestinationType.GROUP]: bin.object({
            address: Address,
          }),
        },
      ),
      groupInvite: bin.optional(WiredGroupInvite),

      text: bin.string,
      attachments: bin.dynamicArrayOf(WiredMessageAttachment),
    }),
    [MessageBodyType.GROUP_JOIN]: bin.object({
      groupAddress: Address,
      groupInvite: WiredGroupInvite,
    }),
    [MessageBodyType.GROUP_LEAVE]: bin.object({
      groupAddress: Address,
    }),
    [MessageBodyType.CONTACT_ADD_NOTIFICATION]: bin.object({
      senderAddress: Address,           // Address of the contact adder
      senderName: bin.string,          // Preferred name of the contact adder 
      senderPublicKey: bin.u8Array(32), // Public key for verification
      timestamp: bin.u64,              // When the contact add occurred
    }),
  },
);

export const WiredMessage = bin.object({
  signature: bin.u8Array(64),
  body: WiredMessageBody,
});

/**
 * Message packet types for chat and file transfer operations
 * 
 * MODERN FILE TRANSFER PATH (Current Implementation):
 * - FILE_METADATA (9): Initial file metadata exchange from sender to receiver
 * - FILE_METADATA_RECEIVED (10): Acknowledgment from receiver to sender
 * - FILE_CHUNK_REQUEST (4): Request for a specific chunk from receiver to sender
 * - FILE_CHUNK_RESPONSE (5): Metadata response confirming chunk availability
 * - FILE_CHUNK_DOWNLOADED (6): Confirmation that a chunk was received and reconstructed
 * - FILE_TRANSFER_STATUS (13): Status updates for transfers (paused, resumed, etc.)
 * - FILE_TRANSFER_ERROR (14): Error reporting between peers
 * 
 * NOTE FOR REFACTORING:
 * This implementation uses a queue-based system with role separation (sender/receiver)
 * and vector clock synchronization. File channel handles binary data only, while chat
 * channel handles all control messages and metadata to ensure reliable delivery and
 * proper ordering of critical messages.
 */
export enum MessagePacketType {
  MESSAGE = 0,
  SYNC = 1,
  GROUP_DETAILS_REQUEST = 2,
  GROUP_DETAILS_RESPONSE = 3,
  FILE_CHUNK_REQUEST = 4,
  FILE_CHUNK_RESPONSE = 5,
  FILE_CHUNK_DOWNLOADED = 6,
  PRESENCE = 7,
  PROFILE_INFO = 8,
  FILE_METADATA = 9,
  FILE_METADATA_RECEIVED = 10,
  FILE_TRANSFER_READY = 11,
  FILE_TRANSFER_READY_FOR_CHUNKS = 12,
  FILE_TRANSFER_STATUS = 13,
  FILE_TRANSFER_ERROR = 14,
  CONTACT_ADDED_NOTIFICATION = 15,
  CONVERSATION_DELETE = 16,
  CONTACT_DELETE = 17,
  MERKLE_TREE_AVAILABLE = 18,
  // GROUP FILE TRANSFER: Phase 1 - Checkpoint-Based Redemption
  GROUP_FILE_METADATA = 19,        // Broadcast file metadata to initialize group transfer
  GROUP_FILE_READY = 20,           // Receiver signals readiness to receive chunks (2-phase handshake)
  GROUP_FILE_CHUNK = 21,           // Broadcast chunk to group members
  GROUP_FILE_CHUNK_ACK = 22,       // Acknowledge chunk receipt (for quorum tracking)
  GROUP_SEEDER_ANNOUNCE = 23,      // Announce availability as seeder after completion
  GROUP_SEEDER_QUERY = 24,         // Query group for available seeders
  GROUP_SEEDER_RESPONSE = 25,      // Response to seeder query with availability info
  // A/V STREAMING: Audio/video call management
  AV_STREAM_OFFER = 26,            // Initiate audio/video call with peer or group
  AV_STREAM_ANSWER = 27,           // Accept incoming audio/video call
  AV_STREAM_ICE_CANDIDATE = 28,    // Exchange ICE candidates for media renegotiation
  AV_STREAM_UPDATE = 29,           // Update stream configuration (quality, type)
  AV_STREAM_END = 30,              // End active audio/video call
  AV_QUALITY_REPORT = 31,          // Share quality metrics and adaptation decisions
  // SDP renegotiation over data channel (when HTTP signaling skipped)
  AV_SDP_OFFER = 32,               // SDP offer for renegotiation via data channel
  AV_SDP_ANSWER = 33,              // SDP answer for renegotiation via data channel
  // GROUP FILE TRANSFER: Phase 2 - Pull-Based Star + Mesh
  GROUP_CHUNK_REQUEST = 34,          // Receiver requests a specific chunk from sender/seeder (pull model)
  // CALENDAR: P2P calendar event sync
  CALENDAR_SYNC = 35,                  // Full calendar event sync (create/update/delete)
  CALENDAR_INVITE = 36,                // Calendar invite/RSVP exchange
  CALENDAR_AVAILABILITY = 37,          // P2P availability request/response (more granular than MongoDB bitmap)
  // STAR TOPOLOGY: Phase 1 — hub-based group calls
  AV_HUB_PROMOTION = 38,                 // Delegate hub role to a higher-tier peer (Tier 1 initiator case)
  // STAR TOPOLOGY: Phase 2A — origin propagation
  GROUP_AV_ROSTER = 39,                  // Hub broadcasts authoritative session membership to leaves
  GROUP_AV_TRACK_ORIGIN = 40,            // Hub tells leaf: "transceiver mid X carries origin Y's track"
}

export const WiredMessagePacket = bin.genericEnum(
  {},
  {
    [MessagePacketType.PRESENCE]: bin.object({
      presenceType: bin.u8, // 0: ping, 1: pong
      timestamp: bin.u64,
      pingId: bin.u32, // For matching pings and pongs
      connectionQuality: bin.optional(bin.u8), // 0-100 connection quality indicator
      browserSessionId: bin.string, // Browser session ID for reload detection
      metadata: bin.string, // Additional diagnostic information
    }),
    [MessagePacketType.PROFILE_INFO]: bin.object({
      nickname: bin.optional(bin.string),      // Display name
      avatarColor: bin.optional(bin.string),   // Hex color for avatar background
      avatarEmoji: bin.optional(bin.string),   // Emoji overlay for avatar
      bannerColor: bin.optional(bin.string),   // Hex color for banner background
      presence: bin.optional(bin.u8),          // 0=online, 1=invisible, 2=dnd
      timestamp: bin.u64,
      publicKey: bin.string,                   // The sender's public key
    }),
    [MessagePacketType.MESSAGE]: bin.object({
      message: WiredMessage,
    }),
    [MessagePacketType.SYNC]: bin.object({
      groupAddress: bin.optional(Address),
      vector: bin.dynamicArrayOf(
        bin.object({
          address: Address,
          nonce: bin.u32,
        }),
      ),
    }),
    [MessagePacketType.GROUP_DETAILS_REQUEST]: bin.object({
      requestNonce: bin.u32,
      groupAddress: Address,
    }),
    [MessagePacketType.GROUP_DETAILS_RESPONSE]: bin.object({
      responseNonce: bin.u32,
      group: bin.optional(
        bin.object({
          version: bin.optional(bin.u32),
          peers: bin.dynamicArrayOf(Address),
          name: bin.optional(bin.string),
        }),
      ),
    }),
    [MessagePacketType.FILE_CHUNK_REQUEST]: bin.object({
      requestNonce: bin.u32,
      downloadNonce: bin.u32,
      sha256: bin.u8Array(32),
      chunkIndex: bin.u32,
    }),
    [MessagePacketType.FILE_CHUNK_RESPONSE]: bin.object({
      responseNonce: bin.u32,
      chunk: bin.optional(
        bin.object({
          proof: bin.dynamicArrayOf(bin.u8Array(32)),
        }),
      ),
    }),
    [MessagePacketType.FILE_CHUNK_DOWNLOADED]: bin.object({
      downloadNonce: bin.u32,
      sessionId: bin.string, // Session ID for tracking
      rootHash: bin.u8Array(32), // File root hash (consistent with sha256)
      vectorClock: bin.string, // JSON serialized vector clock (required for state tracking)
      timestamp: bin.u64, // Timestamp for sequencing events
      symbolsUsed: bin.optional(bin.u32), // EFFICIENCY_TRACKING: symbols needed for adaptive transmission
    }),
    [MessagePacketType.FILE_METADATA]: bin.object({
      requestNonce: bin.u32, // For matching request/response
      sessionId: bin.string, // UUID string for session tracking
      sha256: bin.u8Array(32), // Root hash of the file (consistent with rootHash)
      fileName: bin.string,
      fileSize: bin.u64,
      chunkSize: bin.u32,
      totalChunks: bin.u32,
      mimeType: bin.optional(bin.string),
      vectorClock: bin.string, // JSON serialized vector clock (no longer optional)
      direction: bin.string, // 'inbound' or 'outbound' for reliable role detection
      
      // Extension capabilities of the sender (optional for backward compatibility)
      senderCapabilities: bin.optional(bin.object({
        extensionVersion: bin.optional(bin.string),
        maxFileSize: bin.u64,
        supportedFeatures: bin.object({
          nativeCheckpointing: bin.u8,     // 0: false, 1: true
          adaptiveChunking: bin.u8,
          deduplication: bin.u8,
          backgroundProcessing: bin.u8,
          fileSystemAccess: bin.u8,
        }),
        memoryLimits: bin.object({
          maxMemoryUsage: bin.u64,
          checkpointThreshold: bin.u64,
          cleanupRatio: bin.f32,
        }),
      })),
      
      // Merkle tree availability for massive file validation (Gap #2)
      merkleTreeAvailable: bin.optional(bin.object({
        treeSize: bin.u32,           // Size of the Merkle tree in bytes
        totalChunks: bin.u32,        // Total number of chunks in the tree
        transferPath: bin.u8,        // 0: OPFS, 1: extension
        sessionId: bin.string,       // Session ID for tree identification
      })),
      
      // Phase 2: Merkle tree size for receiver chunk 0 parsing
      merkleTreeSize: bin.optional(bin.u32), // Size of merkle tree in bytes (for chunk 0 padding)
      
      // EMAIL_P2P: Email-initiated transfer flags for receiver UI
      emailInitiated: bin.optional(bin.u8), // 1 if transfer was initiated via email compose
      emailRecipient: bin.optional(bin.string), // Email address of recipient (for display)
      emailSubject: bin.optional(bin.string), // Email subject for scoping attachment cards to specific emails
      // ATTACHMENT_HASH_BRIDGE: The hash used in the chat message attachment.root field.
      // For large files (>400MB) this is a metadata hash that differs from the Merkle root.
      // Receiver stores this so AttachmentCard can match attachment.root → transfer record.
      attachmentHash: bin.optional(bin.string),
    }),
    [MessagePacketType.FILE_METADATA_RECEIVED]: bin.object({
      responseNonce: bin.u32, // Matches the requestNonce from FILE_METADATA
      sessionId: bin.string, // Echo back the session ID
      sha256: bin.u8Array(32), // Echo back the root hash
      status: bin.u8, // 0: accepted, 1: rejected, 2: error
      errorMessage: bin.optional(bin.string),
      
      // Extension capabilities of the receiver (optional for backward compatibility)
      receiverCapabilities: bin.optional(bin.object({
        extensionVersion: bin.optional(bin.string),
        maxFileSize: bin.u64,
        supportedFeatures: bin.object({
          nativeCheckpointing: bin.u8,     // 0: false, 1: true
          adaptiveChunking: bin.u8,
          deduplication: bin.u8,
          backgroundProcessing: bin.u8,
          fileSystemAccess: bin.u8,
        }),
        memoryLimits: bin.object({
          maxMemoryUsage: bin.u64,
          checkpointThreshold: bin.u64,
          cleanupRatio: bin.f32,
        }),
      })),
    }),
    [MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS]: bin.object({
      requestNonce: bin.u32, // For matching request/response
      sessionId: bin.string, // UUID string for session tracking
      sha256: bin.u8Array(32), // Root hash of the file to indicate ready for chunks
      // Vector clock is handled dynamically as it's not directly supported in schema
    }),
    [MessagePacketType.FILE_TRANSFER_READY]: bin.object({
      responseNonce: bin.u32, // Matches requestNonce from FILE_TRANSFER_READY_FOR_CHUNKS
      sessionId: bin.string, // Echo back the session ID
      sha256: bin.u8Array(32), // Echo back the root hash
      status: bin.u8, // 0: accepted, 1: rejected, 2: error
      errorMessage: bin.optional(bin.string),
      // Vector clock is handled dynamically as it's not directly supported in schema
    }),
    [MessagePacketType.FILE_TRANSFER_STATUS]: bin.object({
      sessionId: bin.string,               // Consistent session tracking
      rootHash: bin.u8Array(32),           // File identifier (consistent with sha256)
      vectorClock: bin.string,             // JSON serialized vector clock for state tracking
      timestamp: bin.u64,                  // Event timestamp
      state: bin.string,                   // Current FileTransferStateEnum value
      previousState: bin.optional(bin.string), // Previous state (optional)
      peerAddress: bin.string,             // Peer address for routing
    }),
    [MessagePacketType.FILE_TRANSFER_ERROR]: bin.object({
      sessionId: bin.string,               // Consistent session tracking
      rootHash: bin.u8Array(32),           // File identifier (consistent with sha256)
      vectorClock: bin.string,             // JSON serialized vector clock for state tracking
      timestamp: bin.u64,                  // Event timestamp
      errorMessage: bin.string,            // Human-readable error description
      errorCode: bin.optional(bin.string), // Optional error categorization
      state: bin.string,                   // Current FileTransferStateEnum value
      peerAddress: bin.string,             // Peer address for routing
      // RaptorQ-specific fields for forward error correction
      symbolsReceived: bin.optional(bin.u32), // Number of symbols received before error
      symbolsNeeded: bin.optional(bin.u32),   // Number of symbols needed for reconstruction
      retryRecommended: bin.optional(bin.u8), // Boolean flag (0/1) indicating if retry is recommended
    }),
    [MessagePacketType.CONTACT_ADDED_NOTIFICATION]: bin.object({
      adderAddress: bin.string,            // Address of the peer who added the contact
      adderNickname: bin.optional(bin.string), // Optional nickname given by the adder
      adderPreferredName: bin.string,      // Preferred name of the peer who added the contact
      timestamp: bin.u64,                  // When the contact was added
    }),
    [MessagePacketType.CONVERSATION_DELETE]: bin.object({
      chatAddress: bin.string,             // Address of the conversation being deleted
      deletedBy: bin.string,               // Address of the peer who initiated deletion
      timestamp: bin.u64,                  // When the deletion occurred
      vector: bin.dynamicArrayOf(          // Vector clock at time of deletion
        bin.object({
          address: Address,
          nonce: bin.u32,
        }),
      ),
    }),
    [MessagePacketType.CONTACT_DELETE]: bin.object({
      contactAddress: bin.string,          // Address of the contact being deleted
      deletedBy: bin.string,               // Address of the peer who initiated deletion
      timestamp: bin.u64,                  // When the deletion occurred
      cascadeConversations: bin.u8,        // Boolean flag (0/1) - delete associated conversations too
    }),
    [MessagePacketType.MERKLE_TREE_AVAILABLE]: bin.object({
      transferId: bin.string,              // Transfer ID for the file
      merkleTreeKey: bin.string,           // OPFS key for the Merkle tree storage
      treeSize: bin.u64,                   // Size of the Merkle tree in bytes
      rootHash: bin.dynamicArrayOf(bin.byte), // SHA-256 root hash of the Merkle tree (32 bytes)
      chunkSize: bin.u32,                  // Chunk size used for tree construction
      totalChunks: bin.u32,                // Total number of chunks/leaves in the tree
      transferPath: bin.u8,                // Transfer path: 0=OPFS, 1=extension
      timestamp: bin.u64,                  // When the tree was generated
    }),
    // GROUP FILE TRANSFER: Phase 1 - Checkpoint-Based Redemption Protocol
    [MessagePacketType.GROUP_FILE_METADATA]: bin.object({
      sessionId: bin.string,               // Group transfer session ID
      rootHash: bin.u8Array(32),           // File root hash (SHA-256)
      groupAddress: bin.string,            // Group chat address
      fileName: bin.string,                // Original filename
      fileSize: bin.u64,                   // File size in bytes
      fileType: bin.string,                // MIME type
      chunkSize: bin.u32,                  // Chunk size for this transfer
      totalChunks: bin.u32,                // Total chunks in file
      merkleTreeSize: bin.optional(bin.u32), // Size of serialized Merkle tree (chunk 0) in bytes
      senderAddress: bin.string,           // Address of sender
      timestamp: bin.u64,                  // Metadata timestamp
    }),
    [MessagePacketType.GROUP_FILE_READY]: bin.object({
      sessionId: bin.string,               // Group transfer session ID
      rootHash: bin.u8Array(32),           // File root hash for verification
      receiverAddress: bin.string,         // Address of receiver sending ready signal
      groupAddress: bin.string,            // Group chat address
      timestamp: bin.u64,                  // Ready signal timestamp
    }),
    [MessagePacketType.GROUP_FILE_CHUNK]: bin.object({
      sessionId: bin.string,               // Group transfer session ID
      rootHash: bin.u8Array(32),           // File root hash (SHA-256)
      groupAddress: bin.string,            // Group chat address
      chunkIndex: bin.u32,                 // Index of this chunk
      chunkData: bin.dynamicArrayOf(bin.byte), // Actual chunk data
      totalChunks: bin.u32,                // Total chunks in file
      senderAddress: bin.string,           // Address of broadcast sender
      timestamp: bin.u64,                  // Broadcast timestamp
    }),
    [MessagePacketType.GROUP_FILE_CHUNK_ACK]: bin.object({
      sessionId: bin.string,               // Group transfer session ID
      chunkIndex: bin.u32,                 // Chunk being acknowledged
      receiverAddress: bin.string,         // Address of receiver sending ACK
      timestamp: bin.u64,                  // ACK timestamp
      symbolsUsed: bin.u32,               // Symbols consumed by decoder (adaptive burst feedback)
    }),
    [MessagePacketType.GROUP_SEEDER_ANNOUNCE]: bin.object({
      sessionId: bin.string,               // Group transfer session ID
      rootHash: bin.u8Array(32),           // File root hash
      groupAddress: bin.string,            // Group chat address
      seederAddress: bin.string,           // Address of announcing seeder
      completedChunks: bin.dynamicArrayOf(bin.u32), // Chunk indices available (bitfield alternative)
      timestamp: bin.u64,                  // Announcement timestamp
    }),
    [MessagePacketType.GROUP_SEEDER_QUERY]: bin.object({
      sessionId: bin.string,               // Group transfer session ID
      rootHash: bin.u8Array(32),           // File root hash being queried
      groupAddress: bin.string,            // Group chat address
      requesterAddress: bin.string,        // Address of straggler requesting seeders
      missingChunks: bin.dynamicArrayOf(bin.u32), // Chunks needed by requester
      timestamp: bin.u64,                  // Query timestamp
    }),
    [MessagePacketType.GROUP_SEEDER_RESPONSE]: bin.object({
      sessionId: bin.string,               // Group transfer session ID
      rootHash: bin.u8Array(32),           // File root hash
      seederAddress: bin.string,           // Address of responding seeder
      requesterAddress: bin.string,        // Address of original requester
      availableChunks: bin.dynamicArrayOf(bin.u32), // Chunks available from this seeder
      bandwidth: bin.u32,                  // Estimated bandwidth (bytes/sec) for load balancing
      timestamp: bin.u64,                  // Response timestamp
    }),
    // GROUP FILE TRANSFER: Phase 2 - Pull-Based Star + Mesh
    [MessagePacketType.GROUP_CHUNK_REQUEST]: bin.object({
      sessionId: bin.string,               // Group transfer session ID
      rootHash: bin.u8Array(32),           // File root hash for verification
      groupAddress: bin.string,            // Group chat address
      chunkIndex: bin.u32,                 // Chunk index being requested
      requesterAddress: bin.string,        // Address of requesting receiver
      downloadNonce: bin.u32,              // Nonce for RaptorQ symbol routing
      timestamp: bin.u64,                  // Request timestamp
    }),
    // A/V STREAMING: Audio/video call management
    [MessagePacketType.AV_STREAM_OFFER]: bin.object({
      sessionId: bin.string,               // Media session ID
      streamType: bin.u8,                  // 0: audio, 1: video, 2: audio+video
      proposedQuality: bin.u8,             // 0: low, 1: medium, 2: high, 3: auto
      hasAudio: bin.u8,                    // Boolean flag (0/1)
      hasVideo: bin.u8,                    // Boolean flag (0/1)
      groupAddress: bin.optional(bin.string), // Group address for group calls
      hubAddress: bin.optional(bin.string),   // Star topology: hub peer address
      treeRole: bin.optional(bin.u8),         // Star topology: 0=hub, 1=leaf
      protocolVersion: bin.optional(bin.u8),  // Phase 2A: 1=legacy, 2=origin propagation supported
      timestamp: bin.u64,                  // Offer timestamp
    }),
    [MessagePacketType.AV_STREAM_ANSWER]: bin.object({
      sessionId: bin.string,               // Media session ID
      accepted: bin.u8,                    // Boolean flag (0/1) - call accepted
      streamType: bin.u8,                  // 0: audio, 1: video, 2: audio+video
      quality: bin.u8,                     // 0: low, 1: medium, 2: high, 3: auto
      timestamp: bin.u64,                  // Answer timestamp
    }),
    [MessagePacketType.AV_STREAM_ICE_CANDIDATE]: bin.object({
      sessionId: bin.string,               // Media session ID
      candidate: bin.string,               // ICE candidate string (JSON serialized)
      timestamp: bin.u64,                  // Candidate timestamp
    }),
    [MessagePacketType.AV_STREAM_UPDATE]: bin.object({
      sessionId: bin.string,               // Media session ID
      newQuality: bin.u8,                  // 0: low, 1: medium, 2: high, 3: auto
      reason: bin.string,                  // 'bandwidth_drop', 'high_rtt', 'manual'
      timestamp: bin.u64,                  // Update timestamp
    }),
    [MessagePacketType.AV_STREAM_END]: bin.object({
      sessionId: bin.string,               // Media session ID
      reason: bin.string,                  // 'hangup', 'timeout', 'error', 'network_failure'
      timestamp: bin.u64,                  // End timestamp
    }),
    [MessagePacketType.AV_QUALITY_REPORT]: bin.object({
      sessionId: bin.string,               // Media session ID
      rtt: bin.u32,                        // Round-trip time (ms)
      jitter: bin.u32,                     // Jitter (ms)
      packetLoss: bin.u8,                  // Packet loss percentage (0-100)
      bandwidth: bin.u32,                  // Bandwidth (bytes/sec)
      currentQuality: bin.u8,              // Current quality level
      timestamp: bin.u64,                  // Report timestamp
    }),
    // SDP renegotiation over data channel (when HTTP signaling skipped)
    [MessagePacketType.AV_SDP_OFFER]: bin.object({
      sdp: bin.string,                     // SDP offer string
      timestamp: bin.u64,                  // Offer timestamp
    }),
    [MessagePacketType.AV_SDP_ANSWER]: bin.object({
      sdp: bin.string,                     // SDP answer string
      timestamp: bin.u64,                  // Answer timestamp
    }),
    // CALENDAR: P2P calendar sync (JSON payloads — small & infrequent)
    [MessagePacketType.CALENDAR_SYNC]: bin.object({
      action: bin.string,                  // CalendarSyncAction enum value
      payload: bin.string,                 // JSON serialized CalendarEvent or eventId
      senderAddress: bin.string,           // Sender's public key
      groupAddress: bin.optional(bin.string), // For group calendar events
      timestamp: bin.u64,
    }),
    [MessagePacketType.CALENDAR_INVITE]: bin.object({
      eventId: bin.string,                 // Calendar event UUID
      inviteId: bin.string,                // Invite UUID
      action: bin.string,                  // 'invite' | 'rsvp'
      rsvp: bin.optional(bin.string),      // CalendarRSVP enum value (for RSVP action)
      eventSnapshot: bin.string,           // JSON: { title, startTime, endTime, allDay, organizerName?, groupName? }
      senderAddress: bin.string,
      timestamp: bin.u64,
    }),
    [MessagePacketType.CALENDAR_AVAILABILITY]: bin.object({
      action: bin.string,                  // 'request' | 'response'
      availability: bin.optional(bin.string), // Base64 bitmap (response only)
      timezone: bin.optional(bin.string),  // IANA timezone (response only)
      windowStart: bin.optional(bin.u64),  // Bitmap window start (response only)
      senderAddress: bin.string,
      timestamp: bin.u64,
    }),
    // STAR TOPOLOGY: Hub promotion delegation
    [MessagePacketType.AV_HUB_PROMOTION]: bin.object({
      sessionId: bin.string,               // Media session ID
      groupAddress: bin.string,            // Group address
      peerAddresses: bin.string,           // JSON-encoded string[] of all group members
      initiatorAddress: bin.string,        // Original initiator (moderator tracking)
      timestamp: bin.u64,
    }),
    // STAR TOPOLOGY: Phase 2A — origin propagation
    [MessagePacketType.GROUP_AV_ROSTER]: bin.object({
      sessionId: bin.string,               // Media session ID
      groupAddress: bin.string,            // Group address (denormalized for routing)
      hubAddress: bin.string,              // Authoritative hub for this roster
      members: bin.dynamicArrayOf(bin.string),  // All session members including hub
      rosterVersion: bin.u32,              // Monotonic per session, 0 at start
      reason: bin.string,                  // 'session-start' | 'leaf-join' | 'leaf-leave' | 'hub-promotion' | 'group-end'
      timestamp: bin.u64,
    }),
    [MessagePacketType.GROUP_AV_TRACK_ORIGIN]: bin.object({
      sessionId: bin.string,               // Media session ID
      mid: bin.string,                     // RTCRtpTransceiver.mid identifying the forwarded track
      originAddress: bin.string,           // Address of the leaf whose track this represents
      trackKind: bin.u8,                   // 0=audio, 1=video, 2=data (future)
      relayPath: bin.dynamicArrayOf(bin.string), // [hub] in Phase 2A; [hub, branch, ...] under tree
      timestamp: bin.u64,
    }),
  },
);

export class MessageEntity extends Entity<MobyDB> {
  public signature!: string;

  public from!: {
    address: string;
    nonce: number;
  };

  public chatAddress!: string;

  public timestamp!: Date;
  public version!: number;
  public buffered!: 0 | 1;

  public type!: MessageBodyType;
  public groupInvite?: {
    inviterSignature: string;
    inviterAddress: string;
    groupAddress: string;
    inviteeAddress?: string;
  };
  public text?: string;
  public attachments?: string[];

  public async getAttachments() {
    if (this.attachments === undefined) {
      return;
    }

    const attachments = await this.db.fileMetadatas
      .where("hash")
      .anyOf(this.attachments)
      .toArray();

    return await Promise.all(
      attachments.map(async (attachment) => {
        return {
          hash: attachment.hash,
          entries: await Promise.all(
            Object.entries(attachment.entries).map(async ([root, path]) => {
              const entry = await this.db.files.get({ root });
              if (entry === undefined) {
                throw new Error(`File root ${root} not found`);
              }
              return {
                root,
                path,
                size: entry.size,
                chunkSize: entry.chunkSize,
              };
            }),
          ),
        };
      }),
    );
  }
}

export class ChatEntity extends Entity<MobyDB> {
  public address!: string;
  public type!: MessageDestinationType;
  public name?: string;
  public secret?: string;
  public peers!: string[];
  public nonces!: Record<string, number>;
  public lastMessageSignature?: string;
  public lastMessageTimestamp?: Date;
  public lastMessageVersion?: number;
  
  // Badge system: track unsynced/unread message counts
  // outboundUnsyncedCount: messages WE sent that peer hasn't acknowledged via SYNC
  public outboundUnsyncedCount?: number;
  // inboundUnreadCount: messages WE received but haven't viewed in chat panel
  public inboundUnreadCount?: number;
  // Track the last nonce we know the peer has synced (for outbound calculation)
  public peerSyncedNonces?: Record<string, number>;

  public getNextNonce(userAddress: string) {
    return (this.nonces[userAddress] ?? 0) + 1;
  }

  public getNextVersion() {
    return (this.lastMessageVersion ?? 0) + 1;
  }

  // Badge system helpers
  public getOutboundUnsyncedCount(): number {
    return this.outboundUnsyncedCount ?? 0;
  }

  public getInboundUnreadCount(): number {
    return this.inboundUnreadCount ?? 0;
  }

  public getTotalBadgeCount(): number {
    return this.getOutboundUnsyncedCount() + this.getInboundUnreadCount();
  }

  public async getLastMessage() {
    if (this.lastMessageSignature === undefined) {
      return undefined;
    }

    const lastMessage = await this.db.messages.get({
      signature: this.lastMessageSignature,
    });
    if (lastMessage === undefined) {
      // During conversation deletion, messages are deleted before chat records
      // This is normal and expected, so return undefined instead of throwing
      console.warn(
        `Last message not found (likely deleted) for chat ${this.address}, signature: ${this.lastMessageSignature}`,
      );
      return undefined;
    }
    if (lastMessage.version !== this.lastMessageVersion) {
      throw new Error(
        `Invariant failed: last message version mismatch for chat ${this.address}.`,
      );
    }
    if (
      this.lastMessageTimestamp === undefined ||
      lastMessage.timestamp.getTime() !== this.lastMessageTimestamp.getTime()
    ) {
      throw new Error(
        `Invariant failed: last message timestamp mismatch for chat ${this.address} (${lastMessage.timestamp} !== ${this.lastMessageTimestamp})`,
      );
    }
    return lastMessage;
  }

  // The messages are returned in chronological order, starting from the most recent
  // message. The cursor is the UUID of the last message in the returned set.
  public async getLatestMessages({
    limit = 100,
    cursor,
  }: {
    limit?: number;
    cursor?: { version: number; address: string };
  } = {}) {
    // CRITICAL: Check for conversation tombstone before returning any messages
    // This prevents deleted conversations from being resynced after peer reconnect
    const isTombstoned = await this.db.isConversationTombstoned(this.address);
    if (isTombstoned) {
      console.log(`[TOMBSTONE-BLOCK] Conversation ${this.address?.slice(0, 16)}... is tombstoned, returning empty messages`);
      return [];
    }

    let query;
    if (cursor !== undefined) {
      query = this.db.messages
        .where("[chatAddress+buffered+version+from.address]")
        .between(
          [this.address, 0, Dexie.minKey, Dexie.minKey],
          [this.address, 0, cursor.version, cursor.address],
        )
        .reverse();
    }
    if (query === undefined) {
      query = this.db.messages
        .where("[chatAddress+buffered+version+from.address]")
        .between(
          [this.address, 0, Dexie.minKey, Dexie.minKey],
          [this.address, 0, Dexie.maxKey, Dexie.maxKey],
        )
        .reverse();
    }
    return await query.limit(limit).toArray();
  }

  public async getMissingMessages(
    ourAddress: string,
    vector: Record<string, number>,
    limit: number = 100,
  ) {
    const missing: MessageEntity[] = [];

    for (const peerAddress of [ourAddress, ...this.peers]) {
      if (missing.length >= limit) {
        break;
      }

      const ourNonce = this.nonces[peerAddress] ?? 0;
      const peerNonce = vector[peerAddress] ?? 0;

      if (peerNonce >= ourNonce) {
        continue;
      }

      const messages = await this.db.messages
        .where("[chatAddress+buffered+from.address+from.nonce]")
        .between(
          [this.address, 0, peerAddress, peerNonce],
          [this.address, 0, peerAddress, ourNonce],
          false,
          true,
        )
        .limit(limit - missing.length)
        .toArray();

      missing.push(...messages);
    }

    return missing;
  }

  // GROUP CHAT: Immediate message application for star topology
  // In group chats, participants receive relayed messages from peers they're not directly
  // connected to, so peer-to-peer nonce validation doesn't apply. The organizer already
  // validated the message before relaying it, so we can apply immediately.
  public async applyGroupMessage(
    userAddress: string,
    message: MessageEntity,
  ) {
    // GROUP messages are applied immediately without nonce buffering
    // This is safe because:
    // 1. Organizer validates message before relaying (signature + nonce check)
    // 2. Participants trust the organizer relay in star topology
    // 3. Timestamp/version ordering prevents duplicates
    
    console.log(`[GROUP_APPLY] Applying group message immediately`, {
      from: message.from.address.slice(0, 8),
      nonce: message.from.nonce,
      version: message.version,
      chatAddress: this.address.slice(0, 8)
    });

    // Update nonce tracking for this sender (for future validation)
    if (this.nonces[message.from.address] === undefined || 
        this.nonces[message.from.address] < message.from.nonce) {
      this.nonces[message.from.address] = message.from.nonce;
    }

    // Mark as unbuffered (applied)
    await this.db.messages.update(message, (msg) => {
      msg.buffered = 0;
    });

    // Update chat metadata with latest message
    if (
      this.lastMessageVersion === undefined ||
      this.lastMessageVersion < message.version
    ) {
      this.lastMessageVersion = message.version;
      this.lastMessageSignature = message.signature;
      this.lastMessageTimestamp = message.timestamp;
    }

    await this.db.chats.update(this, (chat) => {
      Object.assign(chat, this);
    });
    
    console.log(`[GROUP_APPLY] Applied group message successfully`, {
      from: message.from.address.slice(0, 8),
      version: message.version,
      lastVersion: this.lastMessageVersion
    });
  }

  // DM (PEER-TO-PEER): Sequential nonce validation for direct connections
  // This MUST NOT be used for group messages - it requires direct peer connections
  public async applyBufferedMessages(
    userAddress: string,
    senderAddress: string,
  ) {
    let senderNonce = this.nonces[senderAddress] ?? 0;

    apply: while (true) {
      const buffered = await this.db.messages
        .where("[chatAddress+buffered+from.address+from.nonce]")
        .between(
          [this.address, 1, senderAddress, senderNonce + 1],
          [this.address, 1, senderAddress, Dexie.maxKey],
          true,
          true,
        )
        .limit(100)
        .toArray();

      if (buffered.length === 0) {
        break apply;
      }

      for (const message of buffered) {
        if (message.from.nonce !== senderNonce + 1) {
          break apply;
        }

        this.applyMessage(userAddress, message);

        await this.db.messages.update(message, (message) => {
          message.buffered = 0;
        });

        if (
          this.lastMessageVersion === undefined ||
          this.lastMessageVersion < message.version
        ) {
          this.lastMessageVersion = message.version;
          this.lastMessageSignature = message.signature;
          this.lastMessageTimestamp = message.timestamp;
        }

        await this.db.chats.update(this, (chat) => {
          Object.assign(chat, this);
        });

        senderNonce++;
      }
    }
  }

  public async applyMessage(userAddress: string, message: MessageEntity) {
    const nextNonce = this.getNextNonce(message.from.address);
    if (message.from.nonce !== nextNonce) {
      throw new Error(
        `Attempted to apply a message ${message.signature} which is from ${message.from.address} with nonce ${message.from.nonce} to chat ${this.address} which has nonce ${this.getNextNonce(message.from.address)}.`,
      );
    }

    if (message.chatAddress !== this.address) {
      throw new Error(
        `Attempted to apply a message ${message.signature} which is meant for chat ${message.chatAddress} to chat ${this.address}.`,
      );
    }

    switch (message.type) {
      case MessageBodyType.MESSAGE: {
        break;
      }
      case MessageBodyType.GROUP_JOIN: {
        if (this.type !== MessageDestinationType.GROUP) {
          throw new Error(
            `Attempted to apply a GROUP_JOIN message for chat ${this.address} which is not a group.`,
          );
        }
        if (message.groupInvite === undefined) {
          throw new Error(
            `Attempted to apply a GROUP_JOIN message for chat ${this.address} which has no group invite.`,
          );
        }

        const senderAddress = message.from.address;
        const groupInviterAddress = message.groupInvite.inviterAddress;
        const groupAddress = message.groupInvite.groupAddress;

        if (groupAddress !== this.address) {
          throw new Error(
            `Attempted to apply a GROUP_JOIN message for chat ${this.address}, but the invite is for group ${groupAddress}.`,
          );
        }

        // Skip adding the invitee to the peers list if the invitee is the current user.
        if (senderAddress !== userAddress) {
          if (
            groupInviterAddress !== userAddress &&
            !this.peers.includes(groupInviterAddress)
          ) {
            throw new Error(
              `Attempted to apply a GROUP_JOIN message for chat ${this.address}, but nobody in the group is the inviter.`,
            );
          }

          if (this.peers.includes(senderAddress)) {
            // TODO(kenta): Your pretentious jargon has been deprecated. Your protocols are now all obsolete. 
            //  We should just include the sender into the group at some point
            //  this is a hack done just for the PoC timeline.
            //  throw new Error(
            //   `Attempted to apply a GROUP_JOIN message for chat ${this.address}, but ${senderAddress} is already part of the group.`,
            // );
            break;
          }

          this.peers.push(senderAddress);
        }

        break;
      }
      case MessageBodyType.GROUP_LEAVE: {
        if (this.type !== MessageDestinationType.GROUP) {
          throw new Error(
            `Attempted to apply a GROUP_LEAVE message for chat ${this.address} which is not a group.`,
          );
        }
        const senderAddress = message.from.address;
        if (senderAddress !== userAddress) {
          if (!this.peers.includes(senderAddress)) {
            throw new Error(
              `Attempted to apply a GROUP_LEAVE message for chat ${this.address}, but ${senderAddress} is not part of the group.`,
            );
          }

          this.peers = this.peers.filter((peer) => peer !== senderAddress);
        }

        break;
      }
      case MessageBodyType.CONTACT_ADD_NOTIFICATION: {
        // CRITICAL: Bidirectional contact notification handler
        // When User A adds User B, this handler runs on User B's side
        
        // Access senderAddress from message.from.address
        const senderAddress = message.from.address;
        
        // Skip self-notifications (should not happen, but defensive)
        if (senderAddress === userAddress) {
          console.log('[ContactAddNotification] Ignoring self-notification');
          break;
        }
        
        // Extract data from the message object - our timestamp is already in the message
        const senderName = message.text || 'Unknown Contact'; // Use text field for name
        const contactTimestamp = message.timestamp;
        
        // Check if contact already exists
        const existingContact = await this.db.contacts.get({ address: senderAddress });
        
        if (!existingContact) {
          // Create new contact from notification (matching existing ContactEntity structure)
          await this.db.contacts.add({
            address: senderAddress,
            card: {
              name: senderName,
              signature: message.signature, // Use message signature for verification
            },
            nickname: "", // User can set this later
            addedAt: contactTimestamp,
          });
          
          console.log(`[ContactAddNotification] Added new contact: ${senderName} (${senderAddress})`);
        } else {
          console.log(`[ContactAddNotification] Contact already exists: ${senderName} (${senderAddress})`);
        }
        
        // Ensure chat instance exists for bidirectional messaging
        let chat = await this.db.chatByAddress(senderAddress);
        if (!chat) {
          // Create DM chat instance automatically
          const chatId = `${userAddress}-${senderAddress}`;
          await this.db.chats.add({
            address: senderAddress,
            type: MessageDestinationType.DM,
            peers: [senderAddress, userAddress],
            nonces: {
              [senderAddress]: 0,
              [userAddress]: 0
            },
            lastMessageVersion: 1,
          });
          
          console.log(`[ContactAddNotification] Created chat instance for: ${senderAddress}`);
        }
        
        // Log successful bidirectional contact establishment
        console.log(`[ContactAddNotification] Bidirectional contact relationship established with ${senderName}`);
        
        break;
      }
      default: {
        throw new Error(
          `Attempted to apply a message ${message.signature} which is of unknown type: ${message.type}`,
        );
      }
    }

    this.nonces[message.from.address] = nextNonce;
  }
}

// It must be guaranteed that:
// 1. roots.length === paths.length
// 2. every root is unique and valid
// 3. every path is unique and valid
// 4. every path maps to exactly one root
export class FileMetadataEntity extends Entity<MobyDB> {
  public hash!: string;
  // paths to roots
  public entries!: { [root: string]: string };
  public roots!: string[];
}

export class FileEntity extends Entity<MobyDB> {
  public root!: string;
  public size!: number;
  public chunkSize!: number;

  public downloaded!: 0 | 1;
  public tree!: Uint8Array[][];
  public chunks!: Set<number>;
}

export class FileTransferEntity extends Entity<MobyDB> {
  public uuid!: string;
  public dir!: "up" | "down";
  public hash!: string;
  public root!: string;
  public index!: number;
  public peerAddress!: string;
  public status!: "pending" | "completed" | "cancelled" | "paused";
  
  // Explicit peer tracking for reliable file transfers
  public senderPeerId?: string;     // The peer sending the file
  public receiverPeerId?: string;   // The peer receiving the file
  public sessionId?: string;        // Unique identifier for this transfer session
  
  // Progress tracking for UI and resume capability
  public progress?: number;          // 0-100 percentage
  public bytesTransferred?: number;  // Number of bytes transferred
  public transferRate?: number;      // Bytes per second
  public resumeData?: string;        // JSON string with resume information
  
  // Merkle tree progress tracking for smart resume (critical for large files)
  // These fields are the source of truth for resume when OPFS merkle tree storage fails
  public completedChunks?: number;   // N - last sequential chunk written
  public totalChunks?: number;       // M - total chunks in file
  public chunkSize?: number;         // Size of each chunk in bytes
  public fileName?: string;          // Original file name for UI recovery
  public fileSize?: number;          // Total file size in bytes
  public lastProgressUpdate?: number; // Timestamp of last progress update
  
  // Context preservation for From/To display after recovery
  public senderAddress?: string;     // Address of the file sender
  public recipientAddress?: string;  // Address of the file recipient
  public createdAt?: number;         // Timestamp when transfer was created
  public updatedAt?: number;         // Timestamp of last update
}

export enum PeerConnectionState {
  CONNECTING = 0,
  CONNECTED = 1,
  DISCONNECTING = 2,
  DISCONNECTED = 3,
  FAILED = 4,
  CIRCUIT_OPEN = 5,   // Temporary disconnection with scheduled recovery
}

export class ContactEntity extends Entity<MobyDB> {
  public address!: string;
  public nickname?: string;
  public card?: {
    name: string;
    signature: string;
  };
  public addedAt!: Date;
  
  // WebRTC connection state
  public connection?: {
    state: RTCPeerConnectionState;
    signalingState: RTCSignalingState;
    iceState: RTCIceConnectionState;
    iceGatheringState: RTCIceGatheringState;
    lastUpdated?: number; // Timestamp when connection state was last updated
    
    // Connection health metrics from WebRTC stats API
    rtt?: number;              // Round-trip time in milliseconds
    jitter?: number;           // Jitter in milliseconds
    packetsLost?: number;      // Total packets lost
    packetsSent?: number;      // Total packets sent (for loss rate calculation)
    bytesSent?: number;        // Total bytes sent
    bytesReceived?: number;    // Total bytes received
    healthScore?: 'green' | 'yellow' | 'red' | 'gray'; // Connection health indicator
    lastStatsUpdate?: number;  // Timestamp of last stats collection
  };
  
  // Enhanced presence tracking
  public presence?: {
    state: PeerConnectionState;
    lastSeen: Date;
    lastHeartbeat: Date;
    connectionQuality: number; // 0-100
    averageRtt: number; // in ms
    sessionId: string; // unique ID for current session
    reconnectAttempts: number;
  };
  
  // Custom properties for storing metadata (like removal info)
  public custom?: Record<string, any>;

  public getDisplayName() {
    // Priority order: nickname (user-set) > card.name (peer's preferredName from ICE) > fallback
    // This ensures ICE bidirectional peer username capture is properly displayed
    if (this.nickname) {
      return this.nickname;
    }
    
    // Use peer's preferredName from ICE bidirectional establishment (stored in card.name)
    if (this.card?.name) {
      return this.card.name;
    }
    
    // Fallback only when no peer name is available from ICE establishment
    const shortAddress = this.address.substring(0, 8);
    return `User-${shortAddress}`;
  }

  public getInitials() {
    return (this.nickname ?? this.card?.name ?? this.address).slice(0, 2);
  }
}

export class OutboxMessageEntity extends Entity<MobyDB> {
  public id!: string; // uuid or unique key
  public toAddress!: string;
  public chatAddress!: string;
  public messageType!: MessagePacketType;
  public payload!: Uint8Array; // serialized message
  public timestamp!: Date;
  public priority!: number; // 0 = normal, higher = urgent
  public status!: 'pending' | 'sending' | 'sent' | 'failed';
  public retryCount!: number;
}

export class EmailGatewayPendingDeliveryEntity extends Entity<MobyDB> {
  public id!: string;
  public recipientAddress!: string;
  public senderAddress!: string;
  public messageType!: string;
  public hasAttachment!: 0 | 1;
  public attachmentRootHash?: string;
  public fileName?: string;
  public fileSize?: number;
  public createdAt!: Date;
  public expiresAt!: Date;
  public delivered!: 0 | 1;
  public deliveredAt?: Date;
 }

/**
 * Pending delivery types for offline P2P sync
 * Lightweight metadata only - actual content delivered via P2P when online
 */
export type PendingDeliveryType = 
  | 'contact_add'      // Contact was added while peer offline
  | 'chat_message'     // Chat message metadata (actual message via P2P)
  | 'file_transfer'    // File transfer metadata (actual file via P2P)
  | 'email_notify'     // Email notification (for MobyNet internal email)
  | 'group_invite'     // Group invitation
  | 'calendar_invite'; // Calendar event invitation (minimal metadata only)

/**
 * General-purpose pending delivery for P2P sync when peers are offline
 * Stores lightweight metadata only - syncs via MongoDB, delivered via P2P
 */
export class PendingDeliveryEntity extends Entity<MobyDB> {
  public id!: string;                    // UUID
  public recipientAddress!: string;      // Recipient's public key
  public senderAddress!: string;         // Sender's public key
  public deliveryType!: PendingDeliveryType;
  public createdAt!: Date;
  public expiresAt!: Date;               // TTL for auto-cleanup (7 days default)
  public delivered!: 0 | 1;              // 0 = pending, 1 = delivered
  public deliveredAt?: Date;
  public syncedToServer!: 0 | 1;         // 0 = local only, 1 = synced to MongoDB
  public serverDeliveryId?: string;      // MongoDB _id for server-side tracking
  
  // Type-specific metadata (lightweight, no actual content)
  public metadata!: {
    // contact_add
    senderName?: string;                 // Display name of contact adder
    senderPublicKey?: string;            // For verification
    
    // chat_message
    chatAddress?: string;                // Chat instance address
    messageSignature?: string;           // Reference to actual message
    hasAttachment?: boolean;
    
    // file_transfer
    rootHash?: string;                   // Merkle root for P2P transfer
    fileName?: string;                   // Display name only
    fileSize?: number;                   // For UI display
    mimeType?: string;
    
    // group_invite
    groupAddress?: string;               // Group to join
    groupName?: string;
    inviterAddress?: string;
    
    // email_notify
    subject?: string;                    // Email subject line
    hasExternalAttachment?: boolean;
    
    // calendar_invite
    eventId?: string;                    // Calendar event UUID
    calendarAction?: 'invite' | 'update' | 'cancel';
    eventTitle?: string;                 // For notification display only
    startTime?: number;                  // Event start (Unix ms)
    endTime?: number;                    // Event end (Unix ms)
    allDay?: boolean;
    organizerName?: string;              // Display name of organizer
    // groupAddress / groupName reuse the group_invite fields above
  };
}

export class EmailGatewayPeerPresenceEntity extends Entity<MobyDB> {
  public address!: string;
  public lastSeen!: Date;
}

/**
 * Pending email invite - tracks invites sent via email before bidirectional contact add
 * The recipient's address is unknown until they accept and we receive CONTACT_ADDED_NOTIFICATION
 */
export type PendingInviteStatus = 'pending' | 'accepted' | 'expired';

export class PendingInviteEntity extends Entity<MobyDB> {
  public id!: string;                    // UUID
  public recipientEmail!: string;        // Email address of invitee
  public senderAddress!: string;         // Our public key (sender)
  public senderKeyString!: string;       // Our key string sent in invite
  public status!: PendingInviteStatus;   // pending, accepted, expired
  public createdAt!: Date;
  public expiresAt!: Date;               // TTL for auto-cleanup (30 days default)
  public personalMessage?: string;       // Optional message sent with invite
  
  // Group invite fields (absent for plain contact invites)
  public groupAddress?: string;          // Group address if this is a group invite
  public groupName?: string;             // Group name for UI display
  
  // Calendar invite fields (absent for plain contact/group invites)
  public calendarEventId?: string;       // Calendar event ID — triggers CALENDAR_INVITE P2P auto-delivery on contact add
  public calendarEventTitle?: string;    // Calendar event title for UI display
  
  // Filled when recipient accepts (bidirectional add completes)
  public recipientAddress?: string;      // Recipient's public key (once known)
  public acceptedAt?: Date;
  
  // Server sync
  public syncedToServer!: 0 | 1;         // 0 = local only, 1 = synced to MongoDB
  public serverInviteId?: string;        // MongoDB _id for server-side tracking
}

export class EmailGatewaySyncStateEntity extends Entity<MobyDB> {
  public key!: string;
  public value!: string;
  public updatedAt!: Date;
 }

/**
 * Entity to track deletion tombstones for synchronized deletion protocol
 * Prevents re-sync of deleted conversations and contacts across peers
 */
export class DeletionTombstoneEntity extends Entity<MobyDB> {
  public id!: string; // uuid
  public entityType!: 'conversation' | 'contact'; // Type of deleted entity
  public entityAddress!: string; // Address of the deleted entity
  public deletedBy!: string; // Address of the peer who initiated deletion
  public timestamp!: Date; // When the deletion occurred
  public vectorClock?: Record<string, number>; // Vector clock at time of deletion (for conversations)
  public cascadeConversations?: boolean; // For contact deletions - whether to cascade to conversations
}

/**
 * Persistent email transfer record for attachment card rendering.
 * Survives page refresh, reconnect, onboarding - the UI reads from this table
 * as a fallback when in-memory transfer Maps are empty.
 *
 * Written by:
 *   - Sender: prepareEmailTransfer / prepareEmailTransferDeferred
 *   - Receiver: FILE_METADATA handler when emailInitiated flag is set
 *   - Migration: checkPendingEmailDeliveries after stale-key migration
 *
 * Read by:
 *   - EmailViewPanel: pendingEmailTransfers useMemo merges in-memory + DB records
 *   - New peer onboarding: after connect, DB records hydrate attachment cards
 */
export class EmailTransferEntity extends Entity<MobyDB> {
  public sessionId!: string;              // Primary key - transfer session UUID
  public recipientEmail!: string;         // Email address the transfer was sent to (lowercase)
  public senderAddress!: string;          // Sender's public key
  public recipientAddress?: string;       // Recipient's public key (may be unknown for deferred)
  public direction!: 'outbound' | 'inbound'; // From our perspective
  public fileName!: string;               // Original file name for UI display
  public fileSize!: number;               // File size in bytes
  public rootHash!: string;               // Merkle root hash (file identifier)
  public state!: string;                  // FileTransferStateEnum value
  public completedChunks?: number;        // Progress counter
  public totalChunks?: number;            // Total chunks
  public chunkSize?: number;              // Chunk size in bytes (for correct rehydration)
  public emailMessageId?: string;         // SMTP message-id for linking to specific email
  public emailSubject?: string;           // Email subject for display context
  public createdAt!: number;              // Timestamp when transfer was initiated
  public updatedAt!: number;              // Last state change timestamp
  public completedAt?: number;            // When transfer finished (completed/failed/cancelled)
  public errorMessage?: string;           // Error message if failed
}

/**
 * Persistent email account credentials for wallet-scoped storage.
 * Replaces fragile localStorage (`moby_pref_emailAccounts`) which is:
 *   - Not wallet-scoped (User B sees User A's accounts on same browser)
 *   - Cleared by browser in low-storage situations
 *   - Lost on browser data reset
 *
 * Security model: Each MobyDB instance is per-wallet (instantiated with wallet address).
 * The wallet login is the security boundary — credentials are only accessible after
 * successful wallet derivation. No additional encryption needed within the DB.
 *
 * Written by:
 *   - EmailDrawer: account CRUD operations
 *   - Onboarding: initial email registration
 *   - Migration: one-time localStorage → IndexedDB migration on session load
 *
 * Read by:
 *   - EmailDrawer: account list, compose "From" selector
 *   - ChatListPanel: IMAP polling for inbox
 *   - EmailViewPanel: compose, reply, attachment download
 *   - EmailGatewaySync: credentials for gateway auth
 */
export class EmailAccountEntity extends Entity<MobyDB> {
  public id!: string;                    // Primary key — UUID
  public email!: string;                 // Email address (e.g. user@gmail.com)
  public imapHost!: string;              // IMAP server hostname
  public imapPort!: number;              // IMAP port (typically 993)
  public smtpHost!: string;              // SMTP server hostname
  public smtpPort!: number;              // SMTP port (typically 587 or 465)
  public username!: string;              // Login username (usually same as email)
  public password!: string;              // App password or IMAP password
  public color!: string;                 // Display color hex for multi-account UI
  public enabled!: number;               // 1 = enabled, 0 = disabled (Dexie doesn't index booleans)
  // OAuth2 support
  public authType!: 'password' | 'oauth2';
  public accessToken?: string;           // OAuth2 access token
  public refreshToken?: string;          // OAuth2 refresh token
  public tokenExpiry?: number;           // Unix timestamp of token expiry
  // MobyChat directory registration
  public registeredWithMobyChat?: number; // 1 = registered, 0 = not (indexable)
  public isPrimary?: number;              // 1 = primary account, 0 = not
  // Timestamps
  public createdAt!: number;             // When account was added
  public updatedAt!: number;             // Last modification
}

/**
 * Calendar event — full meeting details, synced exclusively via P2P data channel.
 * NEVER stored on server. MongoDB only sees availability bitmap (free/busy slots).
 *
 * Written by:
 *   - CalendarDrawer/CalendarPanel: event CRUD
 *   - P2P sync handler: CALENDAR_SYNC messages from peers
 *
 * Read by:
 *   - useCalendarEvents hook: reactive queries for UI
 *   - Header component: next upcoming meeting display
 *   - Availability builder: generates bitmap for MongoDB presence
 */
export class CalendarEventEntity extends Entity<MobyDB> {
  public id!: string;                      // Primary key — UUID
  public title!: string;
  public description?: string;
  public location?: string;                // Free text or URL
  
  // Timing
  public startTime!: number;               // Unix ms
  public endTime!: number;                 // Unix ms
  public allDay!: number;                  // 0 | 1 (Dexie doesn't index booleans)
  public timezone!: string;                // IANA timezone
  
  // Recurrence (JSON serialized RecurrenceRule)
  public recurrence?: string;              // JSON string
  public recurringEventId?: string;        // Parent event ID for recurrence exceptions
  
  // Visibility
  public visibility!: string;              // EventVisibility enum value
  public reminders?: string;               // JSON serialized CalendarReminder[]
  
  // Ownership
  public organizerAddress!: string;        // Creator's public key
  public attendees!: string;               // JSON serialized CalendarAttendee[]
  
  // Group support
  public groupAddress?: string;            // Group chat address if group meeting
  
  // Sync metadata
  public vectorClock!: string;             // JSON serialized Record<string, number>
  public createdAt!: number;               // Unix ms
  public updatedAt!: number;               // Unix ms
  public deletedAt?: number;               // Soft delete for sync (Unix ms)
  
  // UI
  public color?: string;                   // Hex color override
}

/**
 * Calendar invite — tracks invite flow between peers.
 * Local state only; the actual invite delivery uses PendingDelivery for offline
 * and CALENDAR_INVITE message type for online P2P.
 */
export class CalendarInviteEntity extends Entity<MobyDB> {
  public id!: string;                      // Primary key — UUID
  public eventId!: string;                 // References CalendarEventEntity.id
  
  // Parties
  public senderAddress!: string;           // Who sent the invite
  public recipientAddress!: string;        // Who receives it
  
  // State
  public rsvp!: string;                    // CalendarRSVP enum value
  public sentAt!: number;                  // Unix ms
  public respondedAt?: number;             // Unix ms
  
  // Delivery tracking
  public deliveredP2P!: number;            // 0 | 1
  public pendingDeliveryId?: string;       // References PendingDeliveryEntity.id
  
  // Minimal event snapshot for offline display (JSON)
  public eventSnapshot!: string;           // JSON serialized { title, startTime, endTime, allDay, organizerName?, groupName? }
}

/** COMMENTED OUT AS A BACKUP FOR MERKLE TREE RECEIPT FALLBACK IF NEEDED. MAY REMOVE.
 * Entity to persist file transfer state for reconnection recovery
 * Enables resuming transfers after disconnection, browser refresh, or device sleep
 
export class TransferStateEntity extends Entity<MobyDB> {
  public sessionId!: string; // Primary key - unique transfer session ID
  public rootHash!: string; // File identifier (SHA-256 hash)
  public peerAddress!: string; // Address of the remote peer (sender or receiver)
  public direction!: 'inbound' | 'outbound'; // Transfer direction from our perspective
  public state!: string; // FileTransferStateEnum value
  
  // File metadata for recovery
  public fileName!: string;
  public fileSize!: number;
  public mimeType?: string;
  public chunkSize!: number;
  public totalChunks!: number;
  
  // Progress tracking
  public completedChunks!: number[]; // Array of completed chunk indices
  public lastChunkIndex!: number; // Last chunk that was being processed
  public bytesTransferred!: number; // Total bytes successfully transferred
  
  // Recovery data
  public checkpointPath?: string; // OPFS path to checkpoint file
  public transferPath?: 'opfs' | 'extension' | 'memory'; // Transfer mechanism
  public vectorClock?: string; // JSON serialized vector clock
  
  // Timestamps
  public createdAt!: Date; // When transfer started
  public lastUpdated!: Date; // Last state update
  public lastChunkAt?: Date; // Last chunk completion time
  
  // Metadata for UI and recovery logic
  public metadata?: string; // JSON serialized additional metadata
}
*/

export class MobyDB extends Dexie {
  public contacts!: EntityTable<ContactEntity, "address">;
  public chats!: EntityTable<ChatEntity, "address">;
  public messages!: EntityTable<MessageEntity, "signature">;
  public fileMetadatas!: EntityTable<FileMetadataEntity, "hash">;
  public files!: EntityTable<FileEntity, "root">;
  public fileTransfers!: EntityTable<FileTransferEntity, "uuid">;
  public outbox!: EntityTable<OutboxMessageEntity, "id">;
  public emailGatewayPendingDeliveries!: EntityTable<EmailGatewayPendingDeliveryEntity, "id">;
  public emailGatewayPeerPresence!: EntityTable<EmailGatewayPeerPresenceEntity, "address">;
  public emailGatewaySyncState!: EntityTable<EmailGatewaySyncStateEntity, "key">;
  public deletionTombstones!: EntityTable<DeletionTombstoneEntity, "id">;
  public pendingDeliveries!: EntityTable<PendingDeliveryEntity, "id">;
  public pendingInvites!: EntityTable<PendingInviteEntity, "id">;
  public emailTransfers!: EntityTable<EmailTransferEntity, "sessionId">;
  public emailAccounts!: EntityTable<EmailAccountEntity, "id">;
  public calendarEvents!: EntityTable<CalendarEventEntity, "id">;
  public calendarInvites!: EntityTable<CalendarInviteEntity, "id">;
 // public transferStates!: EntityTable<TransferStateEntity, "sessionId">;

  public constructor(name: string, opts?: DexieOptions) {
    super(name, opts);

    this.version(1).stores({
      contacts: "&address, card.name, &nickname, card.signature, addedAt",
      chats: [
        "&address",
        "&group.address",
        "*peers",
        "lastMessageSignature",
        "lastMessageVersion",
        "&[type+address]",
        "[type+lastMessageTimestamp+lastMessageSignature]",
      ].join(", "),
      messages: [
        "&signature",
        "chatAddress",
        "[chatAddress+buffered+version+from.address]",
        "[chatAddress+buffered+from.address+from.nonce]",
        "type",
        "buffered",
        "version",
        "timestamp",
      ].join(", "),
      fileMetadatas: "&hash",
      files: "&root, downloaded",
      fileTransfers: "&uuid, hash, peerAddress, status",
      outbox: "&id, toAddress, chatAddress, status, priority, timestamp",
    });
    
    // Schema update for file transfer improvements
    // Adding root as an indexed field for more reliable file transfer lookups
    this.version(2).stores({
      contacts: "&address, card.name, &nickname, card.signature, addedAt",
      chats: [
        "&address",
        "&group.address",
        "*peers",
        "lastMessageSignature",
        "lastMessageVersion",
        "&[type+address]",
        "[type+lastMessageTimestamp+lastMessageSignature]",
      ].join(", "),
      messages: [
        "&signature",
        "chatAddress",
        "[chatAddress+buffered+version+from.address]",
        "[chatAddress+buffered+from.address+from.nonce]",
        "type",
        "buffered",
        "version",
        "timestamp",
      ].join(", "),
      fileMetadatas: "&hash",
      files: "&root, downloaded",
      fileTransfers: "&uuid, hash, peerAddress, status, root", // Added root index
      outbox: "&id, toAddress, chatAddress, status, priority, timestamp",
    });

    // Schema update for synchronized deletion protocol
    // Adding deletion tombstones to track synchronized deletions across peers
    this.version(3).stores({
      contacts: "&address, card.name, &nickname, card.signature, addedAt",
      chats: [
        "&address",
        "&group.address",
        "*peers",
        "lastMessageSignature",
        "lastMessageVersion",
        "&[type+address]",
        "[type+lastMessageTimestamp+lastMessageSignature]",
      ].join(", "),
      messages: [
        "&signature",
        "chatAddress",
        "[chatAddress+buffered+version+from.address]",
        "[chatAddress+buffered+from.address+from.nonce]",
        "type",
        "buffered",
        "version",
        "timestamp",
      ].join(", "),
      fileMetadatas: "&hash",
      files: "&root, downloaded",
      fileTransfers: "&uuid, hash, peerAddress, status, root",
      outbox: "&id, toAddress, chatAddress, status, priority, timestamp",
      deletionTombstones: "&id, entityType, entityAddress, deletedBy, timestamp, [entityType+entityAddress]", // Added tombstone table
    });

    this.version(4).stores({
      contacts: "&address, card.name, &nickname, card.signature, addedAt",
      chats: [
        "&address",
        "&group.address",
        "*peers",
        "lastMessageSignature",
        "lastMessageVersion",
        "&[type+address]",
        "[type+lastMessageTimestamp+lastMessageSignature]",
      ].join(", "),
      messages: [
        "&signature",
        "chatAddress",
        "[chatAddress+buffered+version+from.address]",
        "[chatAddress+buffered+from.address+from.nonce]",
        "type",
        "buffered",
        "version",
        "timestamp",
      ].join(", "),
      fileMetadatas: "&hash",
      files: "&root, downloaded",
      fileTransfers: "&uuid, hash, peerAddress, status, root",
      outbox: "&id, toAddress, chatAddress, status, priority, timestamp",
      deletionTombstones: "&id, entityType, entityAddress, deletedBy, timestamp, [entityType+entityAddress]",
      emailGatewayPendingDeliveries: "&id, recipientAddress, senderAddress, createdAt, expiresAt, delivered, [recipientAddress+createdAt], [recipientAddress+delivered]",
      emailGatewayPeerPresence: "&address, lastSeen",
      emailGatewaySyncState: "&key, updatedAt",
    });

    // Schema update for general-purpose pending deliveries (P2P sync)
    // Supports offline delivery for contact_add, chat_message, file_transfer, email_notify, group_invite
    this.version(5).stores({
      contacts: "&address, card.name, &nickname, card.signature, addedAt",
      chats: [
        "&address",
        "&group.address",
        "*peers",
        "lastMessageSignature",
        "lastMessageVersion",
        "&[type+address]",
        "[type+lastMessageTimestamp+lastMessageSignature]",
      ].join(", "),
      messages: [
        "&signature",
        "chatAddress",
        "[chatAddress+buffered+version+from.address]",
        "[chatAddress+buffered+from.address+from.nonce]",
        "type",
        "buffered",
        "version",
        "timestamp",
      ].join(", "),
      fileMetadatas: "&hash",
      files: "&root, downloaded",
      fileTransfers: "&uuid, hash, peerAddress, status, root",
      outbox: "&id, toAddress, chatAddress, status, priority, timestamp",
      deletionTombstones: "&id, entityType, entityAddress, deletedBy, timestamp, [entityType+entityAddress]",
      emailGatewayPendingDeliveries: "&id, recipientAddress, senderAddress, createdAt, expiresAt, delivered, [recipientAddress+createdAt], [recipientAddress+delivered]",
      emailGatewayPeerPresence: "&address, lastSeen",
      emailGatewaySyncState: "&key, updatedAt",
      pendingDeliveries: "&id, recipientAddress, senderAddress, deliveryType, createdAt, expiresAt, delivered, syncedToServer, [recipientAddress+delivered], [recipientAddress+deliveryType], [senderAddress+delivered], [syncedToServer+delivered]",
    });

    // Schema update for pending email invites (bidirectional contact add flow)
    // Tracks invites sent via email before recipient's address is known
    this.version(6).stores({
      contacts: "&address, card.name, &nickname, card.signature, addedAt",
      chats: [
        "&address",
        "&group.address",
        "*peers",
        "lastMessageSignature",
        "lastMessageVersion",
        "&[type+address]",
        "[type+lastMessageTimestamp+lastMessageSignature]",
      ].join(", "),
      messages: [
        "&signature",
        "chatAddress",
        "[chatAddress+buffered+version+from.address]",
        "[chatAddress+buffered+from.address+from.nonce]",
        "type",
        "buffered",
        "version",
        "timestamp",
      ].join(", "),
      fileMetadatas: "&hash",
      files: "&root, downloaded",
      fileTransfers: "&uuid, hash, peerAddress, status, root",
      outbox: "&id, toAddress, chatAddress, status, priority, timestamp",
      deletionTombstones: "&id, entityType, entityAddress, deletedBy, timestamp, [entityType+entityAddress]",
      emailGatewayPendingDeliveries: "&id, recipientAddress, senderAddress, createdAt, expiresAt, delivered, [recipientAddress+createdAt], [recipientAddress+delivered]",
      emailGatewayPeerPresence: "&address, lastSeen",
      emailGatewaySyncState: "&key, updatedAt",
      pendingDeliveries: "&id, recipientAddress, senderAddress, deliveryType, createdAt, expiresAt, delivered, syncedToServer, [recipientAddress+delivered], [recipientAddress+deliveryType], [senderAddress+delivered], [syncedToServer+delivered]",
      pendingInvites: "&id, recipientEmail, senderAddress, status, createdAt, expiresAt, syncedToServer, [senderAddress+status], [status+createdAt]",
    });

    // Schema update for email transfer persistence
    // Enables attachment card rendering across page refresh, reconnect, and onboarding
    this.version(7).stores({
      emailTransfers: "&sessionId, recipientEmail, senderAddress, recipientAddress, direction, rootHash, state, createdAt, updatedAt, [recipientEmail+direction], [senderAddress+direction], [state+updatedAt]",
    });

    // Schema update for wallet-scoped email account credential persistence
    // Replaces localStorage (moby_pref_emailAccounts) which is not wallet-scoped
    this.version(8).stores({
      emailAccounts: "&id, email, enabled, authType, registeredWithMobyChat, isPrimary, createdAt, updatedAt",
    });

    // Schema update for calendar invite email flow
    // calendarEventId on pendingInvites enables CALENDAR_INVITE auto-delivery after email contact joins
    this.version(10).stores({
      pendingInvites: "&id, recipientEmail, senderAddress, status, createdAt, expiresAt, syncedToServer, calendarEventId, [senderAddress+status], [status+createdAt], [calendarEventId+status]",
    });

    // Schema update for MobyCalendar P2P calendar system
    // Events synced exclusively via P2P — only availability bitmap touches MongoDB
    this.version(9).stores({
      calendarEvents: "&id, organizerAddress, groupAddress, startTime, endTime, updatedAt, deletedAt, [organizerAddress+startTime], [startTime+endTime]",
      calendarInvites: "&id, eventId, senderAddress, recipientAddress, rsvp, sentAt, [recipientAddress+rsvp], [eventId+recipientAddress]",
    });

/* COMMENTED OUT AS A BACKUP FOR MERKLE TREE RECEIPT FALLBACK IF NEEDED. MAY REMOVE.
    // Schema update for transfer state persistence
    // Adding transferStates table to enable reconnection recovery
    this.version(4).stores({
      contacts: "&address, card.name, &nickname, card.signature, addedAt",
      chats: [
        "&address",
        "&group.address",
        "*peers",
        "lastMessageSignature",
        "lastMessageVersion",
        "&[type+address]",
        "[type+lastMessageTimestamp+lastMessageSignature]",
      ].join(", "),
      messages: [
        "&signature",
        "chatAddress",
        "[chatAddress+buffered+version+from.address]",
        "[chatAddress+buffered+from.address+from.nonce]",
        "type",
        "buffered",
        "version",
        "timestamp",
      ].join(", "),
      fileMetadatas: "&hash",
      files: "&root, downloaded",
      fileTransfers: "&uuid, hash, peerAddress, status, root",
      outbox: "&id, toAddress, chatAddress, status, priority, timestamp",
      deletionTombstones: "&id, entityType, entityAddress, deletedBy, timestamp, [entityType+entityAddress]",
      transferStates: "&sessionId, peerAddress, rootHash, state, direction, [peerAddress+state], [state+lastUpdated], lastUpdated", // Added transfer state persistence
    });
    */

    this.contacts.mapToClass(ContactEntity);
    this.chats.mapToClass(ChatEntity);
    this.messages.mapToClass(MessageEntity);
    this.fileMetadatas.mapToClass(FileMetadataEntity);
    this.files.mapToClass(FileEntity);
    this.fileTransfers.mapToClass(FileTransferEntity);
    this.outbox.mapToClass(OutboxMessageEntity);
    this.emailGatewayPendingDeliveries.mapToClass(EmailGatewayPendingDeliveryEntity);
    this.emailGatewayPeerPresence.mapToClass(EmailGatewayPeerPresenceEntity);
    this.emailGatewaySyncState.mapToClass(EmailGatewaySyncStateEntity);
    this.deletionTombstones.mapToClass(DeletionTombstoneEntity);
    this.pendingDeliveries.mapToClass(PendingDeliveryEntity);
    this.pendingInvites.mapToClass(PendingInviteEntity);
    this.emailTransfers.mapToClass(EmailTransferEntity);
    this.emailAccounts.mapToClass(EmailAccountEntity);
    this.calendarEvents.mapToClass(CalendarEventEntity);
    this.calendarInvites.mapToClass(CalendarInviteEntity);
    //this.transferStates.mapToClass(TransferStateEntity);

    this.on("ready", async (vipdb) => {
      const db = vipdb as MobyDB;

      await db.contacts.toCollection().modify((contact) => {
        contact.connection = undefined;
      });

      await db.fileTransfers
        .where("status")
        .equals("pending")
        .modify((transfer) => {
          transfer.status = "cancelled";
        });
    });
  }

  public async storeContact(contact: {
    address: string;
    nickname?: string;
    card?: {
      name: string;
      signature: string;
    };
    addedAt: Date;
  }) {
    await this.contacts.add(contact);
    
    // Create bi-directional chat instance for both peers (restore pre-health-metrics behavior)
    // This ensures both the contact adder and the added contact have blank chat instances
   
    const userAddress = contact.address; // Temporary fix - using contact address
    let chat = await this.chatByAddress(contact.address);
    if (!chat) {
      // Create DM chat instance automatically for bi-directional messaging
      await this.chats.add({
        address: contact.address,
        type: MessageDestinationType.DM,
        peers: [contact.address, userAddress],
        nonces: {
          [contact.address]: 0,
          [userAddress]: 0
        },
        lastMessageVersion: 1,
      });
      
      console.log(`[BiDirectionalChat] Created chat instance for manual contact add: ${contact.address}`);
    }
  }

  public async getLatestChats({
    type,
    cursor,
    limit = 100,
  }: {
    type?: MessageDestinationType;
    limit?: number;
    cursor?: { timestamp: Date; signature: string };
  } = {}) {
    let query;
    if (cursor !== undefined) {
      query = this.chats
        .where("[type+lastMessageTimestamp+lastMessageSignature]")
        .between(
          [type ?? Dexie.minKey, Dexie.minKey, Dexie.minKey],
          [type ?? Dexie.maxKey, cursor.timestamp, cursor.signature],
        )
        .reverse();
    }
    if (query === undefined) {
      query = this.chats
        .where("[type+lastMessageTimestamp+lastMessageSignature]")
        .between(
          [type ?? Dexie.minKey, Dexie.minKey, Dexie.minKey],
          [type ?? Dexie.maxKey, Dexie.maxKey, Dexie.maxKey],
        )
        .reverse();
    }
    
    const chats = await query.limit(limit).toArray();

    // CRITICAL FIX: Filter out tombstoned conversations
    // This ensures deleted conversations don't appear in UI after deletion
    //
    // P0-I 1S-DB: Two optimizations on the cold-start path that drove
    // chat-list panel suspense starvation on K Linux FF (see
    // docs/connection-desync-audit.md, "Conversations tab lag" section):
    //
    //   (1) Tombstone-existence early-out.  The common case is zero
    //       conversation tombstones; one indexed `first()` lookup tells
    //       us whether the per-chat filter is needed at all.
    //   (2) Parallelize the per-chat filter.  N serial `await` round
    //       trips collapse to one batch via `Promise.all`, eliminating
    //       N forced event-loop yields under main-thread pressure.
    const hasAnyConversationTombstone =
      (await this.deletionTombstones
        .where("entityType")
        .equals("conversation")
        .first()) !== undefined;
    if (!hasAnyConversationTombstone) {
      return chats;
    }

    const tombstoneFlags = await Promise.all(
      chats.map((chat) => this.isConversationTombstoned(chat.address)),
    );
    const activeChats: typeof chats = [];
    for (let i = 0; i < chats.length; i++) {
      if (tombstoneFlags[i]) {
        console.log(
          `[getLatestChats] Filtered out tombstoned conversation ${chats[i]!.address.slice(0, 16)}...`,
        );
      } else {
        activeChats.push(chats[i]!);
      }
    }

    return activeChats;
  }

  public async chatByAddress(address: string) {
    const chat = await this.chats.get({
      address,
    });
    
    // CRITICAL FIX: Don't return tombstoned conversations
    // This prevents deleted conversations from being loaded on refresh
    if (chat) {
      const isTombstoned = await this.isConversationTombstoned(chat.address);
      if (isTombstoned) {
        console.log(`[chatByAddress] Blocked access to tombstoned conversation ${chat.address.slice(0, 16)}...`);
        return undefined;
      }
    }
    
    return chat;
  }

  public async chatByPeerAddress(peerAddress: string) {
    return await this.chats.get({
      type: MessageDestinationType.DM,
      address: peerAddress,
    });
  }

  /**
   * Delete a conversation completely with true synchronized deletion across all participants
   * This ensures that when any peer deletes a conversation, ALL participants delete it
   * @param chatAddress - The address of the chat to delete
   * @param userAddress - The address of the current user (for tombstone creation)
   * @param deletedBy - The address of the peer who initiated deletion (for sync protocol)
   * @param skipNotification - Skip peer notification (used when processing remote deletion)
   * @returns Promise that resolves when deletion is complete
    
    console.log(`[BiDirectionalChat] Created chat instance for manual contact add: ${contact.address}`);
  }
}

public async getLatestChats({
  type,
  cursor,
  limit = 100,
}: {
  type?: MessageDestinationType;
  limit?: number;
  cursor?: { timestamp: Date; signature: string };
} = {}) {
  let query;
  if (cursor !== undefined) {
    query = this.chats
      .where("[type+lastMessageTimestamp+lastMessageSignature]")
      .between(
        [type ?? Dexie.minKey, Dexie.minKey, Dexie.minKey],
        [type ?? Dexie.maxKey, cursor.timestamp, cursor.signature],
      )
      .reverse();
  }
  if (query === undefined) {
    query = this.chats
      .where("[type+lastMessageTimestamp+lastMessageSignature]")
      .between(
        [type ?? Dexie.minKey, Dexie.minKey, Dexie.minKey],
        [type ?? Dexie.maxKey, Dexie.maxKey, Dexie.maxKey],
      )
      .reverse();
  }
  
  const chats = await query.limit(limit).toArray();
  
  // CRITICAL FIX: Filter out tombstoned conversations
  // This ensures deleted conversations don't appear in UI after deletion
  const activeChats = [];
  for (const chat of chats) {
    const isTombstoned = await this.isConversationTombstoned(chat.address);
    if (!isTombstoned) {
      activeChats.push(chat);
    } else {
      console.log(`[getLatestChats] Filtered out tombstoned conversation ${chat.address.slice(0, 16)}...`);
    }
  }
  
  return activeChats;
}

public async chatByAddress(address: string) {
  const chat = await this.chats.get({
    address,
  });
  
  // CRITICAL FIX: Don't return tombstoned conversations
  // This prevents deleted conversations from being loaded on refresh
  if (chat) {
    const isTombstoned = await this.isConversationTombstoned(chat.address);
    if (isTombstoned) {
      console.log(`[chatByAddress] Blocked access to tombstoned conversation ${chat.address.slice(0, 16)}...`);
      return undefined;
    }
  }
  
  return chat;
}

public async chatByPeerAddress(peerAddress: string) {
  return await this.chats.get({
    type: MessageDestinationType.DM,
    address: peerAddress,
  });
}

/**
 * Delete a conversation completely with true synchronized deletion across all participants
 * This ensures that when any peer deletes a conversation, ALL participants delete it
 * @param chatAddress - The address of the chat to delete
 * @param userAddress - The address of the current user (for tombstone creation)
 * @param deletedBy - The address of the peer who initiated deletion (for sync protocol)
 * @param skipNotification - Skip peer notification (used when processing remote deletion)
 * @returns Promise that resolves when deletion is complete
 */
public async deleteConversation(chatAddress: string, userAddress: string, deletedBy?: string, skipNotification = false) {
  console.log(`[SYNCHRONIZED-DELETE] Starting conversation deletion ${chatAddress} (deletedBy: ${deletedBy || 'local'})`);
  
  // Get current vector clock and chat info before deletion
  const chat = await this.chats.get(chatAddress);
  
  // DEBUG: Log database state to diagnose query mismatch
  if (!chat) {
    console.log(`[SYNCHRONIZED-DELETE-DEBUG] Chat not found by direct lookup. Debugging database state...`);
    console.log(`[SYNCHRONIZED-DELETE-DEBUG] Searching for address: ${chatAddress}`);
    
    // Check what chat addresses actually exist in the database
    const allChats = await this.chats.toArray();
    console.log(`[SYNCHRONIZED-DELETE-DEBUG] Found ${allChats.length} total chats in database:`);
    allChats.forEach((existingChat, index) => {
      console.log(`[SYNCHRONIZED-DELETE-DEBUG] Chat ${index}: address=${existingChat.address}, type=${existingChat.type}`);
      if (existingChat.address === chatAddress) {
        console.log(`[SYNCHRONIZED-DELETE-DEBUG] EXACT MATCH FOUND at index ${index}!`);
      }
    });
    
    // Also check if there are any chats with partial address matches
    const partialMatches = allChats.filter(c => 
      c.address.includes(chatAddress.slice(0, 16)) || 
      chatAddress.includes(c.address.slice(0, 16))
    );
    if (partialMatches.length > 0) {
      console.log(`[SYNCHRONIZED-DELETE-DEBUG] Found ${partialMatches.length} partial address matches:`);
      partialMatches.forEach((match, index) => {
        console.log(`[SYNCHRONIZED-DELETE-DEBUG] Partial match ${index}: ${match.address}`);
      });
    }
  }
  
  // CRITICAL FIX: For true multi-peer deletion, we need to ensure complete data cleanup
  // even when the chat doesn't exist locally, because there may be orphaned messages or data
  if (!chat) {
    console.log(`[SYNCHRONIZED-DELETE] Chat ${chatAddress} not found, performing cleanup deletion`);
      
      // Always perform complete cleanup for any deletion (local or remote)
      // This ensures no orphaned data remains that could be resynced later
      
      // Delete any orphaned messages that might exist without a chat record
      const deletedMessages = await this.messages
        .where('chatAddress')
        .equals(chatAddress)
        .delete();
      
      // Delete any orphaned chat records that might exist
      const deletedChats = await this.chats
        .where('address')
        .equals(chatAddress)
        .delete();
      
      console.log(`[SYNCHRONIZED-DELETE] ✓ Cleanup deleted ${deletedMessages} orphaned messages, ${deletedChats} orphaned chats`);
      
      // Create tombstone for deduplication (for both local and remote deletions)
      const tombstoneResult = await this.tryCreateConversationTombstone(
        chatAddress, 
        deletedBy || userAddress, 
        {} // Empty vector clock for already-deleted conversations
      );
      
      console.log(`[SYNCHRONIZED-DELETE] ✓ Completed cleanup deletion of ${chatAddress}`);
    
    // Dispatch UI event for cleanup deletions too (ensures UI consistency)
    const cleanupDeletionEvent = new CustomEvent('moby-conversation-deleted', {
      detail: {
        conversationAddress: chatAddress,
        deletedBy: deletedBy || userAddress,
        deletionCounter: Date.now(),
        tombstoneId: tombstoneResult.tombstoneId,
        cleanupDeletion: true
      }
    });
    
    window.dispatchEvent(cleanupDeletionEvent);
    console.log(`[SYNCHRONIZED-DELETE] ✓ Dispatched cleanup deletion event for ${chatAddress}`);
    
    return {
      deletedMessages,
      deletedChats,
      contactPreserved: true,
      tombstoneCreated: tombstoneResult.created,
      alreadyDeleted: false, // Changed to false since we performed actual cleanup
      synchronizedDeletion: true,
      tombstoneId: tombstoneResult.tombstoneId
    };
    }
    
    const vectorClock = chat.nonces || {};
    
    // STEP 1: Create deletion tombstone FIRST (before any deletion)
    // This prevents race conditions and ensures deletion is tracked
    const tombstoneResult = await this.tryCreateConversationTombstone(
      chatAddress, 
      deletedBy || userAddress, 
      vectorClock
    );
    
    if (!tombstoneResult.created) {
      console.log(`[SYNCHRONIZED-DELETE] ⚠️ Tombstone already exists for ${chatAddress}, skipping deletion`);
      return {
        deletedMessages: 0,
        deletedChats: 0,
        contactPreserved: true,
        tombstoneCreated: false,
        alreadyDeleted: true,
        synchronizedDeletion: true
      };
    }
    
    const tombstoneId = tombstoneResult.tombstoneId!;
    
    // STEP 2: Notify ALL peers BEFORE local deletion (so they can also delete)
    if (!skipNotification && chat) {
      console.log(`[SYNCHRONIZED-DELETE] Broadcasting deletion to all peers in chat ${chatAddress}`);
      await this.broadcastConversationDeletion(chatAddress, deletedBy || userAddress, new Date(), vectorClock);
    }
    
    // STEP 3: Delete ALL local data for this conversation
    // Delete all messages for this chat
    const deletedMessages = await this.messages
      .where('chatAddress')
      .equals(chatAddress)
      .delete();
    
    console.log(`[SYNCHRONIZED-DELETE] ✓ Deleted ${deletedMessages} messages for chat ${chatAddress}`);
    
    // Delete the chat record itself
    const deletedChats = await this.chats
      .where('address')
      .equals(chatAddress)
      .delete();
    
    console.log(`[SYNCHRONIZED-DELETE] ✓ Deleted ${deletedChats} chat records for ${chatAddress}`);
    
    // STEP 4: Contact record is intentionally preserved (conversation deletion ≠ contact deletion)
    console.log(`[SYNCHRONIZED-DELETE] ✓ Contact preserved for chat ${chatAddress}`);
    
    console.log(`[SYNCHRONIZED-DELETE] ✓ Completed synchronized deletion of ${chatAddress}`);
  
  // STEP 5: Dispatch UI event for immediate updates (no refresh required)
  const deletionEvent = new CustomEvent('moby-conversation-deleted', {
    detail: {
      conversationAddress: chatAddress,
      deletedBy: deletedBy || userAddress,
      deletionCounter: Date.now(), // Unique counter for UI refresh triggers
      tombstoneId
    }
  });
  
  window.dispatchEvent(deletionEvent);
  console.log(`[SYNCHRONIZED-DELETE] ✓ Dispatched UI deletion event for ${chatAddress}`);
  
  return {
    deletedMessages,
    deletedChats,
    contactPreserved: true,
    tombstoneCreated: true,
    synchronizedDeletion: true
  };
}

  /**
   * Clear conversation history (local-only, no tombstone, no peer broadcast).
   * 
   * This is a "soft delete" that only removes local chat data:
   * - Messages for this chat
   * - Associated file metadata and file records
   * - File transfer records for this peer
   * - The chat record itself
   * 
   * Contact is preserved. No tombstone is created, so the conversation
   * can be recreated naturally when the peer sends a new message.
   * 
   * Use this for "Delete Conversation" (clear history).
   * Use deleteContact() for "Block User" (permanent removal + tombstone).
   */
  public async clearConversationHistory(chatAddress: string) {
    console.log(`[CLEAR-HISTORY] Clearing conversation history for ${chatAddress}`);

    // Step 1: Collect attachment hashes from messages before deleting them
    const messages = await this.messages
      .where('chatAddress')
      .equals(chatAddress)
      .toArray();

    const attachmentHashes = new Set<string>();
    for (const msg of messages) {
      if (msg.attachments) {
        for (const hash of msg.attachments) {
          attachmentHashes.add(hash);
        }
      }
    }

    // Step 2: Delete file metadata and file records for collected attachments
    let deletedFileMetadatas = 0;
    let deletedFiles = 0;
    if (attachmentHashes.size > 0) {
      const hashes = Array.from(attachmentHashes);

      // Get file metadata entries to find root hashes for file records
      const metadatas = await this.fileMetadatas
        .where('hash')
        .anyOf(hashes)
        .toArray();

      const rootHashes = new Set<string>();
      for (const meta of metadatas) {
        for (const root of Object.keys(meta.entries)) {
          rootHashes.add(root);
        }
      }

      // Delete file records by root hash
      if (rootHashes.size > 0) {
        deletedFiles = await this.files
          .where('root')
          .anyOf(Array.from(rootHashes))
          .delete();
      }

      // Delete file metadata records
      deletedFileMetadatas = await this.fileMetadatas
        .where('hash')
        .anyOf(hashes)
        .delete();

      console.log(`[CLEAR-HISTORY] Deleted ${deletedFileMetadatas} file metadata, ${deletedFiles} file records`);
    }

    // Step 3: Delete file transfer records for this peer
    let deletedTransfers = 0;
    try {
      deletedTransfers = await this.fileTransfers
        .where('peerAddress')
        .equals(chatAddress)
        .delete();
      console.log(`[CLEAR-HISTORY] Deleted ${deletedTransfers} file transfer records`);
    } catch (e) {
      console.warn(`[CLEAR-HISTORY] Could not clean file transfers:`, e);
    }

    // Step 4: Delete all messages
    const deletedMessages = await this.messages
      .where('chatAddress')
      .equals(chatAddress)
      .delete();
    console.log(`[CLEAR-HISTORY] Deleted ${deletedMessages} messages`);

    // Step 5: Delete the chat record
    const deletedChats = await this.chats
      .where('address')
      .equals(chatAddress)
      .delete();
    console.log(`[CLEAR-HISTORY] Deleted ${deletedChats} chat records`);

    // Step 6: Dispatch UI event for immediate refresh
    window.dispatchEvent(new CustomEvent('moby-conversation-deleted', {
      detail: {
        conversationAddress: chatAddress,
        deletedBy: 'local',
        deletionCounter: Date.now(),
        clearHistoryOnly: true
      }
    }));

    console.log(`[CLEAR-HISTORY] ✓ Cleared conversation history for ${chatAddress}`);
    return {
      deletedMessages,
      deletedChats,
      deletedFileMetadatas,
      deletedFiles,
      deletedTransfers,
      contactPreserved: true,
      tombstoneCreated: false
    };
  }

  /**
   * Tombstone check methods for sync protection
   */
  public async isConversationTombstoned(chatAddress: string): Promise<boolean> {
    const tombstone = await this.deletionTombstones
      .where('[entityType+entityAddress]')
      .equals(['conversation', chatAddress])
      .first();
    return tombstone !== undefined;
  }
  
  /**
   * Atomically try to create a conversation tombstone, preventing race conditions
   * Returns true if tombstone was created, false if it already existed
   */
  public async tryCreateConversationTombstone(
    chatAddress: string, 
    deletedBy: string, 
    vectorClock: Record<string, number> = {}
  ): Promise<{ created: boolean; tombstoneId?: string }> {
    const tombstoneId = crypto.randomUUID();
    const tombstone = {
      id: tombstoneId,
      entityType: 'conversation' as const,
      entityAddress: chatAddress,
      deletedBy,
      timestamp: new Date(),
      vectorClock: vectorClock
    };
    
    try {
      await this.deletionTombstones.add(tombstone);
      console.log(`[ATOMIC-TOMBSTONE] ✓ Created tombstone ${tombstoneId} for conversation ${chatAddress}`);
      return { created: true, tombstoneId };
    } catch (error) {
      // If this fails due to unique constraint (duplicate), tombstone already exists
      if (error instanceof Dexie.ConstraintError) {
        console.log(`[ATOMIC-TOMBSTONE] ⚠️ Tombstone already exists for conversation ${chatAddress} (duplicate prevented)`);
        return { created: false };
      }
      // Re-throw other errors
      throw error;
    }
  }
  
  public async isContactTombstoned(contactAddress: string): Promise<boolean> {
    const tombstone = await this.deletionTombstones
      .where('[entityType+entityAddress]')
      .equals(['contact', contactAddress])
      .first();
    return tombstone !== undefined;
  }
  
  public async getTombstoneInfo(entityType: 'conversation' | 'contact', entityAddress: string): Promise<DeletionTombstoneEntity | undefined> {
    return await this.deletionTombstones
      .where('[entityType+entityAddress]')
      .equals([entityType, entityAddress])
      .first();
  }



  /**
   * Broadcast conversation deletion to ALL participants for true synchronized deletion
   * This ensures that when any peer deletes a conversation, ALL participants delete it completely
   * @param chatAddress - Address of the deleted conversation
   * @param deletedBy - Address of the peer who initiated deletion
   * @param timestamp - When the deletion occurred
   * @param vectorClock - Vector clock at time of deletion
   */
  private async broadcastConversationDeletion(
    chatAddress: string, 
    deletedBy: string, 
    timestamp: Date, 
    vectorClock: Record<string, number>
  ) {
    console.log(`[SYNCHRONIZED-DELETE-BROADCAST] Broadcasting synchronized deletion of ${chatAddress} to ALL participants`);
    
    try {
      // Get the chat to find ALL participants (not just "peers")
      const chat = await this.chats.where('address').equals(chatAddress).first();
      if (!chat) {
        console.warn(`[SYNCHRONIZED-DELETE-BROADCAST] Chat ${chatAddress} not found, cannot broadcast deletion`);
        return;
      }

      // Convert vector clock to the expected format
      const vectorArray = Object.entries(vectorClock).map(([address, nonce]) => ({
        address: hexToBytes(address),
        nonce
      }));

      // Create SYNCHRONIZED deletion message payload
      const deletionMessage = {
        type: MessagePacketType.CONVERSATION_DELETE as MessagePacketType.CONVERSATION_DELETE,
        chatAddress,
        deletedBy,
        timestamp: BigInt(timestamp.getTime()),
        vector: vectorArray,
        // Add flag to indicate this is a synchronized deletion (all participants must delete)
        synchronized: true
      };
      
      const encodedPacket = binencode(WiredMessagePacket, deletionMessage);

      // Find ALL participants in this conversation (excluding the peer who initiated deletion)
      const allParticipants = chat.peers || [];
      const participantsToNotify = allParticipants.filter((address: string) => address !== deletedBy);
      
      console.log(`[SYNCHRONIZED-DELETE-BROADCAST] Notifying ${participantsToNotify.length} participants for synchronized deletion:`);
      participantsToNotify.forEach(addr => console.log(`  - ${addr.slice(0, 16)}...`));
      
      // Send synchronized deletion message to ALL connected participants
      let successCount = 0;
      let failedPeers: string[] = [];
      
      for (const participantAddress of participantsToNotify) {
        try {
          // Get node instance for peer communication
          const node = (globalThis as any).node;
          if (!node) {
            console.error(`[SYNCHRONIZED-DELETE-BROADCAST] MobyNode instance not available`);
            failedPeers.push(participantAddress);
            continue;
          }
          
          const peer = node.getOrCreatePeer(participantAddress);
          if (peer && peer.connection.connectionState === 'connected' && peer.chat) {
            await peer.chat.send(encodedPacket);
            console.log(`[SYNCHRONIZED-DELETE-BROADCAST] ✓ Synchronized deletion sent to ${participantAddress.slice(0, 16)}...`);
            successCount++;
          } else {
            console.log(`[SYNCHRONIZED-DELETE-BROADCAST] ✗ Participant ${participantAddress.slice(0, 16)}... not connected, will sync later`);
            failedPeers.push(participantAddress);
          }
        } catch (error) {
          console.error(`[SYNCHRONIZED-DELETE-BROADCAST] Failed to send to participant ${participantAddress.slice(0, 16)}...:`, error);
          failedPeers.push(participantAddress);
        }
      }
      
      console.log(`[SYNCHRONIZED-DELETE-BROADCAST] Synchronized deletion status:`);
      console.log(`  ✓ Successfully notified: ${successCount}/${participantsToNotify.length} participants`);
      if (failedPeers.length > 0) {
        console.log(`  ✗ Failed/offline participants: ${failedPeers.length}`);
        failedPeers.forEach(addr => console.log(`    - ${addr.slice(0, 16)}...`));
        console.log(`  → These participants will receive deletion when they come online`);
      }
      
      // Return status for caller
      return {
        totalParticipants: participantsToNotify.length,
        notifiedParticipants: successCount,
        failedParticipants: failedPeers.length,
        synchronizedDeletion: true
      };
      
    } catch (error) {
      console.error(`[SYNCHRONIZED-DELETE-BROADCAST] Failed to broadcast synchronized deletion:`, error);
      throw error;
    }
  }

  /**
   * Broadcast contact deletion to all peers who have this contact
   * @param contactAddress - Address of the deleted contact
   * @param deletedBy - Address of the peer who initiated deletion
   * @param timestamp - When the deletion occurred
   * @param cascadeConversations - Whether to cascade to conversations
   */
  private async broadcastContactDeletion(
    contactAddress: string, 
    deletedBy: string, 
    timestamp: Date, 
    cascadeConversations: boolean
  ) {
    console.log(`[CONTACT-DELETE-BROADCAST] Broadcasting deletion of contact ${contactAddress} to peers`);
    
    try {
      // Create deletion message payload matching the schema
      const deletionMessage = {
        type: MessagePacketType.CONTACT_DELETE as MessagePacketType.CONTACT_DELETE,
        contactAddress,
        deletedBy,
        timestamp: BigInt(timestamp.getTime()),
        cascadeConversations: cascadeConversations ? 1 : 0 // Convert boolean to u8
      };
      
      const encodedPacket = binencode(WiredMessagePacket, deletionMessage);

      // Get all contacts to find who to notify
      // For contact deletion, we notify all peers in our contact list
      const contacts = await this.contacts.toArray();
      const peersToNotify = contacts
        .map(contact => contact.address)
        .filter((address: string) => address !== deletedBy && address !== contactAddress);
      
      console.log(`[CONTACT-DELETE-BROADCAST] Would notify ${peersToNotify.length} peers about contact deletion of ${contactAddress}`);
      peersToNotify.forEach((peerAddress: string) => {
        console.log(`[CONTACT-DELETE-BROADCAST] -> Peer ${peerAddress}`);
      });
      
      // Return the encoded packet for the MobyNode layer to send
      return { encodedPacket, peersToNotify };
      
    } catch (error) {
      console.error(`[CONTACT-DELETE-BROADCAST] Failed to broadcast contact deletion:`, error);
      throw error;
    }
  }

  /**
   * Delete a contact permanently along with all associated data
   * @param contactAddress - The address of the contact to delete
   * @param userAddress - The address of the current user (for tombstone creation)
   * @param deletedBy - The address of the peer who initiated deletion (for sync protocol)
   * @param skipNotification - Skip peer notification (used when processing remote deletion)
   * @param cascadeConversations - Whether to also delete associated conversations
   * @returns Promise that resolves when deletion is complete
   */
  public async deleteContact(
    contactAddress: string, 
    userAddress: string, 
    deletedBy?: string, 
    skipNotification = false,
    cascadeConversations = true
  ) {
    console.log(`[CONTACT-DELETE] Deleting contact ${contactAddress} and all associated data (deletedBy: ${deletedBy || 'local'})`);
    
    let deletedMessages = 0;
    let deletedChats = 0;
    
    // If cascading conversations, delete messages and chats
    if (cascadeConversations) {
      // First, delete all messages for this contact
      deletedMessages = await this.messages
        .where('chatAddress')
        .equals(contactAddress)
        .delete();
      
      console.log(`[CONTACT-DELETE] Deleted ${deletedMessages} messages for contact ${contactAddress}`);
      
      // Then delete the chat record
      deletedChats = await this.chats
        .where('address')
        .equals(contactAddress)
        .delete();
      
      console.log(`[CONTACT-DELETE] Deleted ${deletedChats} chat records for contact ${contactAddress}`);
    }
    
    // Delete the contact record itself
    const deletedContacts = await this.contacts
      .where('address')
      .equals(contactAddress)
      .delete();
    
    console.log(`[CONTACT-DELETE] Deleted ${deletedContacts} contact records for ${contactAddress}`);
    
    // Create deletion tombstone to prevent re-sync
    const tombstoneId = crypto.randomUUID();
    const tombstone = {
      id: tombstoneId,
      entityType: 'contact' as const,
      entityAddress: contactAddress,
      deletedBy: deletedBy || userAddress,
      timestamp: new Date(),
      cascadeConversations: cascadeConversations
    };
    
    await this.deletionTombstones.add(tombstone);
    console.log(`[CONTACT-DELETE] Created deletion tombstone ${tombstoneId} for ${contactAddress}`);
    
    // Notify peers about the deletion (unless this is from a remote deletion)
    if (!skipNotification) {
      await this.broadcastContactDeletion(contactAddress, tombstone.deletedBy, tombstone.timestamp, cascadeConversations);
    }
    
    return {
      deletedMessages,
      deletedChats,
      deletedContacts,
      permanentlyDeleted: true,
      tombstoneCreated: true,
      cascadedConversations: cascadeConversations
    };
  }

  /**
   * Ensures a chat exists for the given peer address, creating it if necessary
   * Uses role-based creation to prevent database conflicts during simultaneous exchanges
   * @param userAddress - The local user's address
   * @param peerAddress - The remote peer's address
   * @returns The chat entity
   */
  public async ensureChatExists(userAddress: string, peerAddress: string) {
    // First check if the chat already exists
    let chat = await this.chatByPeerAddress(peerAddress);
    
    // CRITICAL: Check for conversation tombstone before creating new chat
    if (chat === undefined) {
      const isTombstoned = await this.isConversationTombstoned(peerAddress);
      if (isTombstoned) {
        console.log(`[TOMBSTONE-BLOCK] ⚠️ Cannot create chat for peer ${peerAddress?.slice(0, 16)}... - conversation is tombstoned (deleted)`);
        return undefined;
      }
    }
    
    // If not tombstoned and doesn't exist, create it immediately for bidirectional contact flow
    if (chat === undefined) {
      console.log('Creating new chat for peer', {
        localUser: userAddress,
        peer: peerAddress,
        timestamp: new Date().toISOString()
      });
      
      try {
        // Add the chat to the database with both addresses as peers
        const chatId = await this.chats.add({
          address: peerAddress,
          type: MessageDestinationType.DM,
          peers: [peerAddress, userAddress],
          nonces: {
            [peerAddress]: 0,
            [userAddress]: 0
          },
          lastMessageVersion: 1,
        });
        
        // Retrieve the newly created chat
        chat = await this.chats.get(chatId);
      } catch (error) {
        // If creation fails due to conflict, try to get existing chat
        console.log('Chat creation conflict detected, retrying fetch', {
          localUser: userAddress,
          peer: peerAddress,
          error: error instanceof Error ? error.message : String(error)
        });
        chat = await this.chatByPeerAddress(peerAddress);
      }
    }
    
    return chat;
  }

  public async chatByGroupAddress(groupAddress: string) {
    return await this.chats.get({
      type: MessageDestinationType.GROUP,
      address: groupAddress,
    });
  }

  public async storeFileMetadataAndRoots(
    attachment: bin.Parsed<typeof WiredMessageAttachment>,
  ) {
    await this.fileMetadatas
      .add({
        hash: bytesToHex(attachment.hash),
        roots: attachment.entries.map((entry) => bytesToHex(entry.root)),
        entries: Object.fromEntries(
          attachment.entries.map((entry) => [
            bytesToHex(entry.root),
            entry.path,
          ]),
        ),
      })
      .catch((err) => {
        if (err instanceof Dexie.ConstraintError) {
          return;
        }
        throw err;
      });

    for (const entry of attachment.entries) {
      await this.files
        .add({
          root: bytesToHex(entry.root),
          size: Number(entry.size),
          chunkSize: entry.chunkSize,

          downloaded: 0,
          tree: [],
          chunks: new Set(),
        })
        .catch((err) => {
          if (err instanceof Dexie.ConstraintError) {
            return;
          }
          throw err;
        });
    }
  }

  public async storeMessage(
    userAddress: string,
    message: bin.Parsed<typeof WiredMessage> & {
      body: { type: MessageBodyType.MESSAGE };
    },
  ) {
    const toAddress = bytesToHex(message.body.to.address);

    const fromAddress = bytesToHex(message.body.from.address);
    const fromNonce = message.body.from.nonce;

    if (fromAddress === toAddress) {
      throw new Error(
        `Attempted to store a message from ${fromAddress} to ${toAddress} where the from address is the same as the to address.`,
      );
    }

    let chat: ChatEntity | undefined;
    switch (message.body.to.type) {
      case MessageDestinationType.DM: {
        let peerAddress: string;
        if (fromAddress === userAddress) {
          peerAddress = toAddress;
        } else {
          peerAddress = fromAddress;
        }

        chat = await this.chatByPeerAddress(peerAddress);

        // Auto-create the chat with the peer.
        if (chat === undefined) {
          await this.chats.add({
            address: peerAddress,
            type: MessageDestinationType.DM,
            peers: [peerAddress],
            nonces: {
              [peerAddress]: 0,
            },
          });

          chat = await this.chatByPeerAddress(peerAddress);
          if (chat === undefined) {
            throw new Error(
              `Direct message chat ${peerAddress} not found after creation`,
            );
          }
        }
        break;
      }
      case MessageDestinationType.GROUP: {
        const groupAddress = toAddress;

        chat = await this.chatByGroupAddress(groupAddress);
        if (chat === undefined) {
          throw new Error(
            `Received a message for a group that we are not part of: ${groupAddress}`,
          );
        }
        if (chat.type !== MessageDestinationType.GROUP) {
          throw new Error(
            `Chat corrupted: group is undefined but chat ${chat.address} got returned when querying for group address ${groupAddress}`,
          );
        }

        // STAR TOPOLOGY VALIDATION & AUTO-MEMBERSHIP UPDATE:
        // In star topology, the organizer relays messages from other participants.
        // When we receive a message from a sender we don't know about, it means:
        // 1. They joined the group (organizer approved their GROUP_JOIN)
        // 2. The organizer is relaying their message to us
        // 3. We haven't synced their GROUP_JOIN message yet
        //
        // Solution: Automatically add unknown senders to our local peers list
        // This allows the star topology to work without requiring immediate GROUP_JOIN sync
        if (fromAddress !== userAddress && !chat.peers.includes(fromAddress)) {
          console.log(`[GROUP_AUTO_MEMBER] Adding unknown sender ${fromAddress.slice(0, 8)} to group ${groupAddress.slice(0, 8)}`, {
            currentPeers: chat.peers.map(p => p.slice(0, 8)),
            newMember: fromAddress.slice(0, 8)
          });
          
          // Add the sender to our local peers list
          chat.peers.push(fromAddress);
          const updatedPeers = [...chat.peers]; // Capture peers for closure
          
          // Persist the updated peers list
          await this.chats.update(chat, (c) => {
            c.peers = updatedPeers;
          });
          
          console.log(`[GROUP_AUTO_MEMBER] Updated group ${groupAddress.slice(0, 8)} peers:`, updatedPeers.map(p => p.slice(0, 8)));
        }
        break;
      }
    }

    let groupInvite;
    if (message.body.groupInvite !== undefined) {
      groupInvite = {
        inviterSignature: bytesToHex(message.body.groupInvite.inviterSignature),
        inviterAddress: bytesToHex(
          message.body.groupInvite.body.inviterAddress,
        ),
        groupAddress: bytesToHex(message.body.groupInvite.body.groupAddress),
        inviteeAddress:
          message.body.groupInvite.body.inviteeAddress !== undefined
            ? bytesToHex(message.body.groupInvite.body.inviteeAddress)
            : undefined,
      };
    }

    await this.messages.put({
      signature: bytesToHex(message.signature),
      chatAddress: chat.address,
      buffered: 1,
      from: {
        address: fromAddress,
        nonce: fromNonce,
      },
      version: message.body.version,
      timestamp: new Date(Number(message.body.timestamp)),
      type: MessageBodyType.MESSAGE,
      groupInvite,
      text: message.body.text,
      attachments: message.body.attachments.map((attachment) =>
        bytesToHex(attachment.hash),
      ),
    });

    await Promise.all(
      message.body.attachments.map((attachment) =>
        this.storeFileMetadataAndRoots(attachment),
      ),
    );

    // CRITICAL: Use different message application strategies for DM vs GROUP
    // DM: Sequential nonce validation (peer-to-peer connection)
    // GROUP: Immediate application (star topology relay)
    if (chat.type === MessageDestinationType.GROUP) {
      // Get the stored message to apply it
      const storedMessage = await this.messages.get({ signature: bytesToHex(message.signature) });
      if (storedMessage) {
        await chat.applyGroupMessage(userAddress, storedMessage);
      } else {
        console.error('[GROUP_APPLY] Failed to find stored message', {
          signature: bytesToHex(message.signature).slice(0, 16)
        });
      }
    } else {
      // DM: Use peer-to-peer nonce validation
      await chat.applyBufferedMessages(userAddress, fromAddress);
    }
  }

  // An invitee would send a GROUP_JOIN message to the inviter.
  // If the invite is valid, the inviter would persist and broadcast the GROUP_JOIN message
  //    to all other peers in the group.
  // If the invite is not valid, the inviter would ignore the message.
  public async storeGroupJoinMessage(
    userAddress: string,
    message: bin.Parsed<typeof WiredMessage> & {
      body: { type: MessageBodyType.GROUP_JOIN };
    },
  ) {
    const fromAddress = bytesToHex(message.body.from.address);
    const fromNonce = message.body.from.nonce;

    const groupAddress = bytesToHex(message.body.groupAddress);
    const inviteGroupAddress = bytesToHex(
      message.body.groupInvite.body.groupAddress,
    );

    if (inviteGroupAddress !== groupAddress) {
      throw new Error(
        `Received a GROUP_JOIN message for group ${groupAddress}, but the invite is for group ${inviteGroupAddress}.`,
      );
    }

    if (message.body.groupInvite.body.inviteeAddress !== undefined) {
      const groupInviteeAddress = bytesToHex(
        message.body.groupInvite.body.inviteeAddress,
      );
      if (groupInviteeAddress !== fromAddress) {
        throw new Error(
          `Received a GROUP_JOIN message from ${fromAddress} for group ${groupAddress}, but the invite is not for them.`,
        );
      }
    }

    const chat = await this.chatByGroupAddress(groupAddress);
    if (chat === undefined) {
      throw new Error(
        `Received a GROUP_JOIN message for a group that we are not part of: ${groupAddress}`,
      );
    }
    if (chat.type !== MessageDestinationType.GROUP) {
      throw new Error(
        `Chat corrupted: group is undefined but chat ${chat.address} got returned when querying for group address ${groupAddress}`,
      );
    }

    await this.messages.put({
      signature: bytesToHex(message.signature),
      chatAddress: chat.address,
      buffered: 1,
      from: {
        address: fromAddress,
        nonce: fromNonce,
      },
      version: message.body.version,
      timestamp: new Date(Number(message.body.timestamp)),
      type: MessageBodyType.GROUP_JOIN,
      groupInvite: {
        inviterSignature: bytesToHex(message.body.groupInvite.inviterSignature),
        inviterAddress: bytesToHex(
          message.body.groupInvite.body.inviterAddress,
        ),
        groupAddress: bytesToHex(message.body.groupInvite.body.groupAddress),
        inviteeAddress:
          message.body.groupInvite.body.inviteeAddress !== undefined
            ? bytesToHex(message.body.groupInvite.body.inviteeAddress)
            : undefined,
      },
    });

    // GROUP: Use immediate application for GROUP_JOIN (same as regular group messages)
    const storedJoinMessage = await this.messages.get({ signature: bytesToHex(message.signature) });
    if (storedJoinMessage) {
      await chat.applyGroupMessage(userAddress, storedJoinMessage);
    } else {
      console.error('[GROUP_JOIN] Failed to find stored message', {
        signature: bytesToHex(message.signature).slice(0, 16)
      });
    }

    // CRITICAL: Add joiner to chat.peers so GROUP_RELAY can route messages to/from them.
    // applyGroupMessage only updates nonces/metadata — it does NOT modify peers[].
    // Without this, new participants are invisible to the relay until they send their
    // first regular message (which triggers GROUP_AUTO_MEMBER as a fallback).
    if (fromAddress !== userAddress && !chat.peers.includes(fromAddress)) {
      chat.peers.push(fromAddress);
      const updatedPeers = [...chat.peers];
      await this.chats.update(chat, (c) => {
        c.peers = updatedPeers;
      });
      console.log(`[GROUP_JOIN] Added ${fromAddress.slice(0, 8)} to group ${groupAddress.slice(0, 8)} peers`, {
        peerCount: updatedPeers.length,
      });
    }
  }

  // A group member would send a GROUP_LEAVE message to the group.
  // If the sender is not part of the group, the message is ignored.
  // Otherwise, the sender is removed from the group and the GROUP_LEAVE message is broadcast to all other peers in the group.
  public async storeGroupLeaveMessage(
    userAddress: string,
    message: bin.Parsed<typeof WiredMessage> & {
      body: { type: MessageBodyType.GROUP_LEAVE };
    },
  ) {
    const fromAddress = bytesToHex(message.body.from.address);
    const fromNonce = message.body.from.nonce;

    const groupAddress = bytesToHex(message.body.groupAddress);

    const chat = await this.chatByGroupAddress(groupAddress);
    if (chat === undefined) {
      throw new Error(
        `Received a GROUP_LEAVE message for a group that we are not part of: ${groupAddress}`,
      );
    }

    await this.messages.put({
      signature: bytesToHex(message.signature),
      chatAddress: chat.address,
      buffered: 1,
      from: {
        address: fromAddress,
        nonce: fromNonce,
      },
      version: message.body.version,
      timestamp: new Date(Number(message.body.timestamp)),
      type: MessageBodyType.GROUP_LEAVE,
    });

    // GROUP: Use immediate application for GROUP_LEAVE (same as regular group messages)
    const storedLeaveMessage = await this.messages.get({ signature: bytesToHex(message.signature) });
    if (storedLeaveMessage) {
      await chat.applyGroupMessage(userAddress, storedLeaveMessage);
    } else {
      console.error('[GROUP_LEAVE] Failed to find stored message', {
        signature: bytesToHex(message.signature).slice(0, 16)
      });
    }
  }

  /** COMMENTED OUT AS A BACKUP FOR MERKLE TREE RECEIPT FALLBACK IF NEEDED. MAY REMOVE.
   * Save or update transfer state for reconnection recovery
   * Call this on transfer start, chunk completion, and state transitions
   
  public async saveTransferState(
    sessionId: string,
    transfer: {
      rootHash: string;
      peerAddress: string;
      direction: 'inbound' | 'outbound';
      state: string;
      metadata: {
        name: string;
        size: number;
        mimeType?: string;
        chunkSize?: number;
        totalChunks?: number;
        transferPath?: 'opfs' | 'extension' | 'memory';
        checkpointPath?: string;
      };
      completedChunks?: Set<number>;
      lastChunkIndex?: number;
      bytesTransferred?: number;
      vectorClock?: Map<string, number>;
    }
  ): Promise<void> {
    const now = new Date();
    const completedChunksArray = transfer.completedChunks 
      ? Array.from(transfer.completedChunks) 
      : [];
    
    const vectorClockJson = transfer.vectorClock
      ? JSON.stringify(Array.from(transfer.vectorClock.entries()))
      : undefined;

    const existingState = await this.transferStates.get({ sessionId });
    
    if (existingState) {
      // Update existing transfer state
      await this.transferStates.update(sessionId, {
        state: transfer.state,
        completedChunks: completedChunksArray,
        lastChunkIndex: transfer.lastChunkIndex ?? existingState.lastChunkIndex,
        bytesTransferred: transfer.bytesTransferred ?? existingState.bytesTransferred,
        lastUpdated: now,
        lastChunkAt: completedChunksArray.length > (existingState.completedChunks?.length ?? 0) ? now : existingState.lastChunkAt,
        vectorClock: vectorClockJson,
      });
    } else {
      // Create new transfer state
      await this.transferStates.add({
        sessionId,
        rootHash: transfer.rootHash,
        peerAddress: transfer.peerAddress,
        direction: transfer.direction,
        state: transfer.state,
        fileName: transfer.metadata.name,
        fileSize: transfer.metadata.size,
        mimeType: transfer.metadata.mimeType,
        chunkSize: transfer.metadata.chunkSize ?? 1048576,
        totalChunks: transfer.metadata.totalChunks ?? 0,
        completedChunks: completedChunksArray,
        lastChunkIndex: transfer.lastChunkIndex ?? 0,
        bytesTransferred: transfer.bytesTransferred ?? 0,
        checkpointPath: transfer.metadata.checkpointPath,
        transferPath: transfer.metadata.transferPath,
        vectorClock: vectorClockJson,
        createdAt: now,
        lastUpdated: now,
        lastChunkAt: completedChunksArray.length > 0 ? now : undefined,
      });
    }
  }

  
   // Get incomplete transfers for a specific peer
   // Used during peer reconnection to recover transfers
   
  public async getIncompleteTransfersForPeer(peerAddress: string): Promise<TransferStateEntity[]> {
    return await this.transferStates
      .where('[peerAddress+state]')
      .between(
        [peerAddress, 'PREPARING'],
        [peerAddress, 'TRANSFERRING']
      )
      .or('[peerAddress+state]')
      .equals([peerAddress, 'PAUSED'])
      .or('[peerAddress+state]')
      .equals([peerAddress, 'READY_FOR_TRANSFER'])
      .toArray();
  }

  
   // Get all incomplete transfers (for global recovery on app start)
   
  public async getAllIncompleteTransfers(): Promise<TransferStateEntity[]> {
    return await this.transferStates
      .where('state')
      .anyOf(['PREPARING', 'TRANSFERRING', 'PAUSED', 'READY_FOR_TRANSFER'])
      .toArray();
  }

  
   // Delete transfer state (call on completion or cancellation)
   
  public async deleteTransferState(sessionId: string): Promise<void> {
    await this.transferStates.delete(sessionId);
  }

  
   // Clean up stale transfer states (older than X hours with no updates)
   
  public async cleanupStaleTransferStates(maxAgeHours: number = 24): Promise<number> {
    const cutoffTime = new Date(Date.now() - maxAgeHours * 60 * 60 * 1000);
    
    const staleTransfers = await this.transferStates
      .where('lastUpdated')
      .below(cutoffTime)
      .toArray();
    
    for (const transfer of staleTransfers) {
      await this.transferStates.delete(transfer.sessionId);
    }
    
    console.log(`[CLEANUP] Removed ${staleTransfers.length} stale transfer states older than ${maxAgeHours} hours`);
    return staleTransfers.length;
  }
    */

  // ============================================================================
  // PENDING DELIVERIES - P2P Sync for offline peers
  // ============================================================================

  /**
   * Queue a pending delivery for an offline peer
   * Stores lightweight metadata only - actual content delivered via P2P when online
   */
  public async queuePendingDelivery(delivery: {
    recipientAddress: string;
    senderAddress: string;
    deliveryType: PendingDeliveryType;
    metadata: PendingDeliveryEntity['metadata'];
    expiresInDays?: number;
  }): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + (delivery.expiresInDays ?? 7) * 24 * 60 * 60 * 1000);

    await this.pendingDeliveries.add({
      id,
      recipientAddress: delivery.recipientAddress,
      senderAddress: delivery.senderAddress,
      deliveryType: delivery.deliveryType,
      createdAt: now,
      expiresAt,
      delivered: 0,
      syncedToServer: 0,
      metadata: delivery.metadata,
    });

    console.log(`[PendingDelivery] Queued ${delivery.deliveryType} for ${delivery.recipientAddress.slice(0, 8)}...`);
    return id;
  }

  /**
   * Get all pending deliveries for a recipient (when they come online)
   */
  public async getPendingDeliveriesForRecipient(recipientAddress: string): Promise<PendingDeliveryEntity[]> {
    return await this.pendingDeliveries
      .where('[recipientAddress+delivered]')
      .equals([recipientAddress, 0])
      .toArray();
  }

  /**
   * Get pending deliveries by type for a recipient
   */
  public async getPendingDeliveriesByType(
    recipientAddress: string,
    deliveryType: PendingDeliveryType
  ): Promise<PendingDeliveryEntity[]> {
    return await this.pendingDeliveries
      .where('[recipientAddress+deliveryType]')
      .equals([recipientAddress, deliveryType])
      .filter(d => d.delivered === 0)
      .toArray();
  }

  /**
   * Get all outgoing pending deliveries from this sender that need server sync
   */
  public async getUnsyncedPendingDeliveries(): Promise<PendingDeliveryEntity[]> {
    return await this.pendingDeliveries
      .where('[syncedToServer+delivered]')
      .equals([0, 0])
      .toArray();
  }

  /**
   * Mark a pending delivery as synced to server
   */
  public async markPendingDeliverySynced(id: string, serverDeliveryId: string): Promise<void> {
    await this.pendingDeliveries.update(id, {
      syncedToServer: 1,
      serverDeliveryId,
    });
  }

  /**
   * Mark a pending delivery as delivered (after P2P sync completes)
   */
  public async markPendingDeliveryDelivered(id: string): Promise<void> {
    await this.pendingDeliveries.update(id, {
      delivered: 1,
      deliveredAt: new Date(),
    });
    console.log(`[PendingDelivery] Marked ${id.slice(0, 8)}... as delivered`);
  }

  /**
   * Delete a pending delivery (after successful delivery or expiry)
   */
  public async deletePendingDelivery(id: string): Promise<void> {
    await this.pendingDeliveries.delete(id);
  }

  /**
   * Clean up expired pending deliveries
   */
  public async cleanupExpiredPendingDeliveries(): Promise<number> {
    const now = new Date();
    const expired = await this.pendingDeliveries
      .where('expiresAt')
      .below(now)
      .toArray();

    for (const delivery of expired) {
      await this.pendingDeliveries.delete(delivery.id);
    }

    if (expired.length > 0) {
      console.log(`[PendingDelivery] Cleaned up ${expired.length} expired deliveries`);
    }
    return expired.length;
  }

  /**
   * Clean up delivered pending deliveries (after grace period)
   */
  public async cleanupDeliveredPendingDeliveries(gracePeriodHours: number = 24): Promise<number> {
    const cutoff = new Date(Date.now() - gracePeriodHours * 60 * 60 * 1000);
    const delivered = await this.pendingDeliveries
      .where('delivered')
      .equals(1)
      .filter(d => d.deliveredAt !== undefined && d.deliveredAt < cutoff)
      .toArray();

    for (const delivery of delivered) {
      await this.pendingDeliveries.delete(delivery.id);
    }

    if (delivered.length > 0) {
      console.log(`[PendingDelivery] Cleaned up ${delivered.length} old delivered entries`);
    }
    return delivered.length;
  }

  // Badge System: Unsynced/Unread Message Count Management
  public async incrementOutboundUnsyncedCount(chatAddress: string): Promise<void> {
    await this.chats.where('address').equals(chatAddress)
      .modify((chat) => { chat.outboundUnsyncedCount = (chat.outboundUnsyncedCount ?? 0) + 1; });
  }

  public async incrementInboundUnreadCount(chatAddress: string): Promise<void> {
    await this.chats.where('address').equals(chatAddress)
      .modify((chat) => { chat.inboundUnreadCount = (chat.inboundUnreadCount ?? 0) + 1; });
  }

  public async clearInboundUnreadCount(chatAddress: string): Promise<void> {
    await this.chats.where('address').equals(chatAddress)
      .modify((c) => { c.inboundUnreadCount = 0; });
  }

  public async decrementOutboundUnsyncedCount(chatAddress: string, count: number = 1): Promise<void> {
    await this.chats.where('address').equals(chatAddress)
      .modify((chat) => { chat.outboundUnsyncedCount = Math.max(0, (chat.outboundUnsyncedCount ?? 0) - count); });
  }
}
